import { defineStore } from 'pinia';
import { MESSAGE_TYPE, MESSAGE_STATUS } from "../api/enums.js"
import useUserStore from './userStore.js';
import useFriendStore from './friendStore.js';
import useGroupStore from './groupStore.js';
import * as messageUtil from '../api/messageUtil.js';
import { getDB } from '../db/index.js';
import {
	resolveGroupChatDisplayName,
	resolveGroupMemberDisplayName,
	resolvePrivateDisplayName
} from '../utils/identityDisplay.js';
import {
	buildContactSearchIndex,
	CONTACT_SEARCH_TEXT_KEY,
	mergeContactSearchIndexes
} from '../utils/contactSearchMatcher.js';
import { ensureStableMessageLocalId } from '../utils/localMessageId.js';
import { mergeMessageRemovalCutoffs, applyGroupMessageRemoval } from '../api/groupMessageCleanup.js';

const LOCAL_URL_PATTERN = /(blob:|data:)/i;
const TRANSIENT_MESSAGE_KEYS = [
	'localPreviewContent',
	'localAssetState',
	'localAssetText',
	'localAssetRetryable'
];

function isTransientLocalMessage(msg) {
	if (!msg || msg.id) {
		return false;
	}
	return typeof msg.content === 'string' && LOCAL_URL_PATTERN.test(msg.content);
}

function resolveMessageDedupKeys(msg) {
	if (!msg || msg.type == MESSAGE_TYPE.TIP_TIME) {
		return [];
	}
	const keys = [];
	if (msg.id !== undefined && msg.id !== null && msg.id !== '') {
		keys.push(`id:${msg.id}`);
	}
	if (msg.selfSend && msg.tmpId !== undefined && msg.tmpId !== null && msg.tmpId !== '') {
		keys.push(`tmp:${msg.tmpId}`);
	}
	if (msg.localId !== undefined && msg.localId !== null && msg.localId !== '') {
		keys.push(`local:${msg.localId}`);
	}
	if (msg.seqNo !== undefined && msg.seqNo !== null && msg.seqNo !== '') {
		keys.push(`seq:${msg.seqNo}`);
	}
	return keys;
}

function resolvePreparedContactSearchIndex(source) {
	if (!source || typeof source !== 'object') {
		return null;
	}
	return source[CONTACT_SEARCH_TEXT_KEY]
		? source[CONTACT_SEARCH_TEXT_KEY]
		: buildContactSearchIndex(source);
}

function prepareChatSearchText(chat, contact = null) {
	if (!chat || typeof chat !== 'object') {
		return chat;
	}
	const chatSearchIndex = buildContactSearchIndex(chat);
	if (chat.type != 'PRIVATE') {
		chat.searchText = chatSearchIndex;
		return chat;
	}
	const contactSearchIndex = resolvePreparedContactSearchIndex(contact);
	chat.searchText = contactSearchIndex
		? mergeContactSearchIndexes(chatSearchIndex, contactSearchIndex)
		: chatSearchIndex;
	return chat;
}

function normalizeDisplayText(value) {
	return String(value ?? '').trim();
}

function pickUsableGroupDisplayName(...values) {
	for (const value of values) {
		const text = normalizeDisplayText(value);
		if (text) {
			return text;
		}
	}
	return '';
}

function resolveUsableGroupChatDisplayName(group) {
	return pickUsableGroupDisplayName(
		resolveGroupChatDisplayName(group),
		group && group.remarkGroupName,
		group && group.name
	);
}

function removeLegacyCompanyName(target) {
	if (!hasOwnField(target, 'companyName')) {
		return false;
	}
	delete target.companyName;
	return true;
}

function resolveMessageSequence(msg) {
	if (!msg) {
		return 0;
	}
	const seqNo = Number(msg.seqNo || msg.id || msg.sendTime || 0);
	return Number.isFinite(seqNo) ? seqNo : 0;
}

function resolvePreferredMessageStatus(currentStatus, nextStatus) {
	const current = Number(currentStatus);
	const next = Number(nextStatus);
	if (Number.isFinite(current) && Number.isFinite(next)) {
		return Math.max(current, next);
	}
	if (Number.isFinite(next)) {
		return next;
	}
	return currentStatus;
}

function mergeDuplicateMessage(target, source) {
	if (!target || !source) {
		return false;
	}
	const previousStatus = target.status;
	const stableLocalId = target.localId;
	const stableLocalPreviewContent = target.localPreviewContent;
	Object.assign(target, source);
	if (stableLocalId) {
		target.localId = stableLocalId;
	}
	if (stableLocalPreviewContent && !target.localPreviewContent) {
		target.localPreviewContent = stableLocalPreviewContent;
	}
	target.status = resolvePreferredMessageStatus(previousStatus, source.status);
	return true;
}

function parseRecallContent(msgInfo) {
	if (msgInfo && msgInfo.recallTombstone) return null;
	try {
		const content = JSON.parse(msgInfo.content);
		const silent = content && content.silent === true;
		const hasTip = content && content.tip !== undefined && content.tip !== null && content.tip !== '';
		if (!content || content.id === undefined || content.id === null || (!silent && !hasTip)) {
			console.log('parseRecallContent invalid,message:', msgInfo.content);
			return null;
		}
		return {
			id: content.id,
			silent,
			tip: hasTip
				? (typeof content.tip === 'string' ? content.tip : JSON.stringify(content.tip))
				: ''
		};
	} catch (e) {
		console.log('parseRecallContent error,message:', msgInfo && msgInfo.content, e);
		return null;
	}
}

function isCurrentUserGroupModerator(groupId) {
	const groupStore = useGroupStore();
	const userStore = useUserStore();
	const group = groupStore.findGroup(groupId);
	const userId = userStore.userInfo && userStore.userInfo.id;
	return Boolean(group && userId &&
		(group.ownerId == userId || groupStore.isManager(group.id, userId)));
}

function shouldSilenceRecall(recallContent, chatInfo) {
	if (!chatInfo || chatInfo.type != 'GROUP') {
		return false;
	}
	return Boolean(recallContent && recallContent.silent) ||
		!isCurrentUserGroupModerator(chatInfo.targetId);
}

function applyRecallTargetState(message, recallContent, silent) {
	if (!message) {
		return;
	}
	message.status = MESSAGE_STATUS.RECALL;
	message.content = silent ? '' : recallContent.tip;
	message.type = silent ? MESSAGE_TYPE.RECALL : MESSAGE_TYPE.TIP_TEXT;
	if (silent) {
		message.recallTombstone = true;
	} else if (hasOwnField(message, 'recallTombstone')) {
		delete message.recallTombstone;
	}
}

function normalizeStoredGroupRecall(message, chatInfo) {
	if (!message || !chatInfo || chatInfo.type != 'GROUP' ||
		message.status != MESSAGE_STATUS.RECALL || message.type == MESSAGE_TYPE.RECALL) {
		return false;
	}
	if (message.type == MESSAGE_TYPE.TIP_TEXT && isCurrentUserGroupModerator(chatInfo.targetId)) {
		return false;
	}
	applyRecallTargetState(message, { tip: '' }, true);
	return true;
}

function sanitizePersistedMessages(messages = []) {
	let changed = false;
	const sanitized = [];
	const dedupIndexMap = new Map();
	messages.forEach((msg) => {
		if (!msg) {
			changed = true;
			return;
		}
		if (isTransientLocalMessage(msg)) {
			changed = true;
			return;
		}
		let nextMessage = msg;
		if (msg.status == MESSAGE_STATUS.SENDING) {
			nextMessage = {
				...msg,
				status: MESSAGE_STATUS.FAILED
			};
			changed = true;
		}
		const dedupKeys = resolveMessageDedupKeys(nextMessage);
		const existingKey = dedupKeys.find(key => dedupIndexMap.has(key));
		if (existingKey) {
			mergeDuplicateMessage(sanitized[dedupIndexMap.get(existingKey)], nextMessage);
			changed = true;
			return;
		}
		sanitized.push(nextMessage);
		dedupKeys.forEach(key => dedupIndexMap.set(key, sanitized.length - 1));
	});
	return {
		changed,
		messages: sanitized
	};
}

function rebuildMessagesWithTimeTips(messages = []) {
	const normalizedMessages = messages
		.filter(msg => msg && msg.type != MESSAGE_TYPE.TIP_TIME)
		.sort((a, b) => {
			const sendTimeDiff = Number(a.sendTime || 0) - Number(b.sendTime || 0);
			if (sendTimeDiff !== 0) {
				return sendTimeDiff;
			}
			return Number(a.id || 0) - Number(b.id || 0);
		});
	const rebuilt = [];
	let lastTimeTip = 0;
	normalizedMessages.forEach(msg => {
		if (!lastTimeTip || lastTimeTip < msg.sendTime - 600 * 1000) {
			rebuilt.push({
				sendTime: msg.sendTime,
				type: MESSAGE_TYPE.TIP_TIME
			});
			lastTimeTip = msg.sendTime;
		}
		rebuilt.push(msg);
	});
	return {
		messages: rebuilt,
		lastTimeTip
	};
}

function hasOwnField(source, key) {
	return Object.prototype.hasOwnProperty.call(source || {}, key);
}

function syncChatMeta(chat, source, keys) {
	if (!chat || !source) {
		return false;
	}
	let changed = false;
	keys.forEach((key) => {
		if (!hasOwnField(source, key)) {
			return;
		}
		if (chat[key] === source[key]) {
			return;
		}
		chat[key] = source[key];
		changed = true;
	});
	return changed;
}

function findLatestChatMessage(messages = []) {
	for (let idx = messages.length - 1; idx >= 0; idx--) {
		const message = messages[idx];
		if (!message || message.type == MESSAGE_TYPE.TIP_TIME || message.type == MESSAGE_TYPE.RECALL) {
			continue;
		}
		return message;
	}
	return null;
}

function findEarliestChatMessage(messages = []) {
	for (let idx = 0; idx < messages.length; idx++) {
		const message = messages[idx];
		if (!message || message.type == MESSAGE_TYPE.TIP_TIME) {
			continue;
		}
		return message;
	}
	return null;
}

function resolveChatLookupKey(type, targetId) {
	return `${String(type || '')}:${targetId === undefined || targetId === null ? '' : String(targetId)}`;
}

let chatLookup = new Map();
let groupSenderChatKeys = new Map();
let groupChatSenderCounts = new Map();
let groupChatSenderMessageLocalIds = new Map();
let chatMessageRuntimeIndexes = new Map();
let pendingSummaryChatKeys = new Set();
let dirtyChatKeys = new Set();
let dirtyMessageLocalIdsByChatKey = new Map();

function resolveBadgeUnreadCount(chat, chatStore) {
	if (!chat || chat.delete || (chatStore && chatStore.isDnd(chat))) {
		return 0;
	}
	const unreadCount = Number(chat.unreadCount || 0);
	return Number.isFinite(unreadCount) && unreadCount > 0 ? unreadCount : 0;
}

function recalculateTotalUnreadCount(chats = [], chatStore) {
	return chats.reduce((total, chat) => total + resolveBadgeUnreadCount(chat, chatStore), 0);
}

function applyChatBadgeMutation(chatStore, chat, mutate) {
	if (!chat || typeof mutate !== 'function') {
		return false;
	}
	const before = resolveBadgeUnreadCount(chat, chatStore);
	const result = mutate();
	const after = resolveBadgeUnreadCount(chat, chatStore);
	if (before !== after && chatStore) {
		chatStore.totalUnreadCount = Math.max(0, Number(chatStore.totalUnreadCount || 0) + after - before);
	}
	return result;
}

function resetChatLookup(chats = []) {
	chatLookup = new Map();
	chats.forEach((chat) => {
		if (!chat) {
			return;
		}
		chatLookup.set(resolveChatLookupKey(chat.type, chat.targetId), chat);
	});
}

function compareChats(chat1, chat2, chatStore) {
	if (chatStore && chatStore.isTop(chat1) && !chatStore.isTop(chat2)) {
		return -1;
	}
	if (chatStore && chatStore.isTop(chat2) && !chatStore.isTop(chat1)) {
		return 1;
	}
	return Number(chat2 && chat2.optTime || 0) - Number(chat1 && chat1.optTime || 0);
}

function sortChatsInPlace(chats = [], chatStore) {
	chats.sort((chat1, chat2) => compareChats(chat1, chat2, chatStore));
}

function hasPendingPersistenceMutations() {
	return dirtyChatKeys.size > 0 || dirtyMessageLocalIdsByChatKey.size > 0 || pendingDeletedLocalIds.size > 0 || pendingClearedConversationKeys.size > 0 ||
		dirtyConversationStateKeys.size > 0 || replaceConversationStatesPending || cursorConfigsDirty;
}

function repositionChatInPlace(chats = [], chat, chatStore) {
	if (!chat || chats.length < 2) {
		return false;
	}
	const currentIndex = chats.indexOf(chat);
	if (currentIndex < 0) {
		return false;
	}
	let insertIndex = 0;
	for (let index = 0; index < chats.length; index++) {
		const current = chats[index];
		if (current === chat) {
			continue;
		}
		if (compareChats(chat, current, chatStore) < 0) {
			break;
		}
		insertIndex++;
	}
	if (currentIndex === insertIndex) {
		return false;
	}
	chats.splice(currentIndex, 1);
	chats.splice(insertIndex, 0, chat);
	return true;
}

function resolveGroupSenderChatKey(chat) {
	if (!chat || chat.type != 'GROUP') {
		return '';
	}
	return resolveChatLookupKey(chat.type, chat.targetId);
}

function collectMessageSenderIds(message) {
	const senderIds = [];
	if (message && message.sendId !== undefined && message.sendId !== null && message.sendId !== '') {
		senderIds.push(String(message.sendId));
	}
	if (message && message.quoteMessage && message.quoteMessage.sendId !== undefined &&
		message.quoteMessage.sendId !== null && message.quoteMessage.sendId !== '') {
		senderIds.push(String(message.quoteMessage.sendId));
	}
	return Array.from(new Set(senderIds));
}

function clearGroupChatSenderIndex(chat) {
	const chatKey = resolveGroupSenderChatKey(chat);
	if (!chatKey) {
		return;
	}
	const senderCounts = groupChatSenderCounts.get(chatKey);
	if (senderCounts) {
		senderCounts.forEach((_, senderId) => {
			const chatKeys = groupSenderChatKeys.get(senderId);
			if (!chatKeys) {
				return;
			}
			chatKeys.delete(chatKey);
			if (chatKeys.size === 0) {
				groupSenderChatKeys.delete(senderId);
			}
		});
	}
	groupChatSenderCounts.delete(chatKey);
	groupChatSenderMessageLocalIds.delete(chatKey);
}

function resolveGroupChatSenderRefs(chatKey, create = false) {
	let senderMessageLocalIds = groupChatSenderMessageLocalIds.get(chatKey);
	if (!senderMessageLocalIds && create) {
		senderMessageLocalIds = new Map();
		groupChatSenderMessageLocalIds.set(chatKey, senderMessageLocalIds);
	}
	return senderMessageLocalIds;
}

function indexGroupChatMessage(chat, message, runtimeState = null) {
	const chatKey = resolveGroupSenderChatKey(chat);
	if (!chatKey || !message || message.type == MESSAGE_TYPE.TIP_TIME) {
		return;
	}
	const resolvedState = runtimeState || captureMessageRuntimeState(chat, message);
	if (!resolvedState || !Array.isArray(resolvedState.senderIds) || resolvedState.senderIds.length === 0) {
		return;
	}
	const senderIds = resolvedState.senderIds;
	let senderCounts = groupChatSenderCounts.get(chatKey);
	if (!senderCounts) {
		senderCounts = new Map();
		groupChatSenderCounts.set(chatKey, senderCounts);
	}
	const senderMessageLocalIds = resolveGroupChatSenderRefs(chatKey, true);
	senderIds.forEach((senderId) => {
		const nextCount = Number(senderCounts.get(senderId) || 0) + 1;
		senderCounts.set(senderId, nextCount);
		let chatKeys = groupSenderChatKeys.get(senderId);
		if (!chatKeys) {
			chatKeys = new Set();
			groupSenderChatKeys.set(senderId, chatKeys);
		}
		chatKeys.add(chatKey);
		if (!resolvedState.localId) {
			return;
		}
		let localIds = senderMessageLocalIds.get(senderId);
		if (!localIds) {
			localIds = new Set();
			senderMessageLocalIds.set(senderId, localIds);
		}
		localIds.add(resolvedState.localId);
	});
}

function unindexGroupChatMessage(chat, runtimeState = null) {
	const chatKey = resolveGroupSenderChatKey(chat);
	const resolvedState = runtimeState;
	if (!chatKey || !resolvedState || !Array.isArray(resolvedState.senderIds) || resolvedState.senderIds.length === 0) {
		return;
	}
	const senderCounts = groupChatSenderCounts.get(chatKey);
	if (!senderCounts) {
		return;
	}
	const senderMessageLocalIds = resolveGroupChatSenderRefs(chatKey);
	resolvedState.senderIds.forEach((senderId) => {
		const nextCount = Number(senderCounts.get(senderId) || 0) - 1;
		if (senderMessageLocalIds && resolvedState.localId) {
			const localIds = senderMessageLocalIds.get(senderId);
			if (localIds) {
				localIds.delete(resolvedState.localId);
				if (localIds.size === 0) {
					senderMessageLocalIds.delete(senderId);
				}
			}
		}
		if (nextCount > 0) {
			senderCounts.set(senderId, nextCount);
			return;
		}
		senderCounts.delete(senderId);
		const chatKeys = groupSenderChatKeys.get(senderId);
		if (!chatKeys) {
			return;
		}
		chatKeys.delete(chatKey);
		if (chatKeys.size === 0) {
			groupSenderChatKeys.delete(senderId);
		}
	});
	if (senderCounts.size === 0) {
		groupChatSenderCounts.delete(chatKey);
	}
	if (senderMessageLocalIds && senderMessageLocalIds.size === 0) {
		groupChatSenderMessageLocalIds.delete(chatKey);
	}
}

function rebuildGroupChatSenderIndex(chat) {
	clearGroupChatSenderIndex(chat);
	if (!chat || chat.type != 'GROUP' || !Array.isArray(chat.messages)) {
		return;
	}
	chat.messages.forEach((message) => indexGroupChatMessage(chat, message));
}

function resetGroupChatSenderIndex(chats = []) {
	groupSenderChatKeys = new Map();
	groupChatSenderCounts = new Map();
	groupChatSenderMessageLocalIds = new Map();
	chats.forEach((chat) => {
		if (chat && chat.type == 'GROUP') {
			rebuildGroupChatSenderIndex(chat);
		}
	});
}

function resolveMessageRuntimeChatKey(chat) {
	return chat ? resolveChatLookupKey(chat.type, chat.targetId) : '';
}

function createMessageRuntimeIndex() {
	return {
		byId: new Map(),
		byTmpId: new Map(),
		byLocalId: new Map(),
		bySeqNo: new Map(),
		quoteRefsByTargetId: new Map()
	};
}

function resolveMessageRuntimeIndex(chat, create = false) {
	const chatKey = resolveMessageRuntimeChatKey(chat);
	if (!chatKey) {
		return null;
	}
	let runtimeIndex = chatMessageRuntimeIndexes.get(chatKey);
	if (!runtimeIndex && create) {
		runtimeIndex = createMessageRuntimeIndex();
		chatMessageRuntimeIndexes.set(chatKey, runtimeIndex);
	}
	return runtimeIndex;
}

function clearMessageRuntimeIndex(chat) {
	const chatKey = resolveMessageRuntimeChatKey(chat);
	if (!chatKey) {
		return;
	}
	chatMessageRuntimeIndexes.delete(chatKey);
}

function resolveQuotedMessageId(message) {
	if (!message || !message.quoteMessage || message.quoteMessage.id === undefined || message.quoteMessage.id === null || message.quoteMessage.id === '') {
		return '';
	}
	return String(message.quoteMessage.id);
}

function captureMessageRuntimeState(chat, message) {
	if (!message || message.type == MESSAGE_TYPE.TIP_TIME) {
		return null;
	}
	const localId = ensurePersistedLocalId(message, chat);
	return {
		id: message.id !== undefined && message.id !== null && message.id !== '' ? String(message.id) : '',
		tmpId: message.tmpId !== undefined && message.tmpId !== null && message.tmpId !== '' ? String(message.tmpId) : '',
		selfSend: Boolean(message.selfSend),
		localId,
		seqNo: message.seqNo !== undefined && message.seqNo !== null && message.seqNo !== '' ? String(message.seqNo) : '',
		quoteTargetId: resolveQuotedMessageId(message),
		senderIds: collectMessageSenderIds(message)
	};
}

function addQuoteRuntimeRef(runtimeIndex, quoteTargetId, localId) {
	if (!runtimeIndex || !quoteTargetId || !localId) {
		return;
	}
	let localIds = runtimeIndex.quoteRefsByTargetId.get(quoteTargetId);
	if (!localIds) {
		localIds = new Set();
		runtimeIndex.quoteRefsByTargetId.set(quoteTargetId, localIds);
	}
	localIds.add(localId);
}

function removeQuoteRuntimeRef(runtimeIndex, quoteTargetId, localId) {
	if (!runtimeIndex || !quoteTargetId || !localId) {
		return;
	}
	const localIds = runtimeIndex.quoteRefsByTargetId.get(quoteTargetId);
	if (!localIds) {
		return;
	}
	localIds.delete(localId);
	if (localIds.size === 0) {
		runtimeIndex.quoteRefsByTargetId.delete(quoteTargetId);
	}
}

function indexRuntimeMessage(chat, message) {
	if (!message || message.type == MESSAGE_TYPE.TIP_TIME) {
		return;
	}
	const runtimeIndex = resolveMessageRuntimeIndex(chat, true);
	const runtimeState = captureMessageRuntimeState(chat, message);
	if (!runtimeIndex || !runtimeState) {
		return;
	}
	if (runtimeState.id) {
		runtimeIndex.byId.set(runtimeState.id, message);
	}
	if (runtimeState.tmpId && runtimeState.selfSend) {
		runtimeIndex.byTmpId.set(runtimeState.tmpId, message);
	}
	if (runtimeState.localId) {
		runtimeIndex.byLocalId.set(runtimeState.localId, message);
	}
	if (runtimeState.seqNo) {
		runtimeIndex.bySeqNo.set(runtimeState.seqNo, message);
	}
	addQuoteRuntimeRef(runtimeIndex, runtimeState.quoteTargetId, runtimeState.localId);
}

function unindexRuntimeMessage(chat, message, runtimeState = null) {
	if (!message || message.type == MESSAGE_TYPE.TIP_TIME) {
		return;
	}
	const runtimeIndex = resolveMessageRuntimeIndex(chat);
	const resolvedState = runtimeState || captureMessageRuntimeState(chat, message);
	if (!runtimeIndex || !resolvedState) {
		return;
	}
	if (resolvedState.id && runtimeIndex.byId.get(resolvedState.id) === message) {
		runtimeIndex.byId.delete(resolvedState.id);
	}
	if (resolvedState.tmpId && resolvedState.selfSend && runtimeIndex.byTmpId.get(resolvedState.tmpId) === message) {
		runtimeIndex.byTmpId.delete(resolvedState.tmpId);
	}
	if (resolvedState.localId && runtimeIndex.byLocalId.get(resolvedState.localId) === message) {
		runtimeIndex.byLocalId.delete(resolvedState.localId);
	}
	if (resolvedState.seqNo && runtimeIndex.bySeqNo.get(resolvedState.seqNo) === message) {
		runtimeIndex.bySeqNo.delete(resolvedState.seqNo);
	}
	removeQuoteRuntimeRef(runtimeIndex, resolvedState.quoteTargetId, resolvedState.localId);
}

function resolveMessageDedupKeysFromState(runtimeState) {
	if (!runtimeState) {
		return [];
	}
	const keys = [];
	if (runtimeState.id) {
		keys.push(`id:${runtimeState.id}`);
	}
	if (runtimeState.tmpId && runtimeState.selfSend) {
		keys.push(`tmp:${runtimeState.tmpId}`);
	}
	if (runtimeState.localId) {
		keys.push(`local:${runtimeState.localId}`);
	}
	if (runtimeState.seqNo) {
		keys.push(`seq:${runtimeState.seqNo}`);
	}
	return keys;
}

function rebuildMessageRuntimeIndex(chat) {
	clearMessageRuntimeIndex(chat);
	if (!chat || !Array.isArray(chat.messages)) {
		return;
	}
	chat.messages.forEach((message) => indexRuntimeMessage(chat, message));
}

function resetMessageRuntimeIndex(chats = []) {
	chatMessageRuntimeIndexes = new Map();
	chats.forEach((chat) => rebuildMessageRuntimeIndex(chat));
}

function findMessageByRuntimeIndex(chat, msgInfo) {
	if (!chat || !msgInfo) {
		return null;
	}
	const runtimeIndex = resolveMessageRuntimeIndex(chat);
	if (!runtimeIndex) {
		return null;
	}
	const hasServerId = msgInfo.id !== undefined && msgInfo.id !== null && msgInfo.id !== '';
	if (hasServerId) {
		const indexedById = runtimeIndex.byId.get(String(msgInfo.id));
		if (indexedById) {
			return indexedById;
		}
	}
	// Acknowledgements can arrive before the server id is indexed. Fall back
	// to the pending message, but never merge two different server ids.
	if (msgInfo.localId !== undefined && msgInfo.localId !== null && msgInfo.localId !== '') {
		const indexedByLocalId = runtimeIndex.byLocalId.get(String(msgInfo.localId));
		if (indexedByLocalId && (!hasServerId || !indexedByLocalId.id || String(indexedByLocalId.id) === String(msgInfo.id))) {
			return indexedByLocalId;
		}
	}
	if (msgInfo.selfSend && msgInfo.tmpId !== undefined && msgInfo.tmpId !== null && msgInfo.tmpId !== '') {
		const indexedByTmpId = runtimeIndex.byTmpId.get(String(msgInfo.tmpId));
		if (indexedByTmpId && (!hasServerId || !indexedByTmpId.id || String(indexedByTmpId.id) === String(msgInfo.id))) {
			return indexedByTmpId;
		}
	}
	if (chat.type == 'SYSTEM' && msgInfo.seqNo !== undefined && msgInfo.seqNo !== null && msgInfo.seqNo !== '') {
		return runtimeIndex.bySeqNo.get(String(msgInfo.seqNo)) || null;
	}
	return null;
}

function hasResolvableMessageLookupKey(chat, msgInfo) {
	if (!msgInfo) {
		return false;
	}
	if (msgInfo.localId !== undefined && msgInfo.localId !== null && msgInfo.localId !== '') {
		return true;
	}
	if (msgInfo.id !== undefined && msgInfo.id !== null && msgInfo.id !== '') {
		return true;
	}
	if (msgInfo.selfSend && msgInfo.tmpId !== undefined && msgInfo.tmpId !== null && msgInfo.tmpId !== '') {
		return true;
	}
	if (chat && chat.type == 'SYSTEM' &&
		msgInfo.seqNo !== undefined && msgInfo.seqNo !== null && msgInfo.seqNo !== '') {
		return true;
	}
	return false;
}

function findMessageQuoteRefs(chat, targetId) {
	if (!chat || targetId === undefined || targetId === null || targetId === '') {
		return [];
	}
	const runtimeIndex = resolveMessageRuntimeIndex(chat);
	if (!runtimeIndex) {
		return [];
	}
	const localIds = runtimeIndex.quoteRefsByTargetId.get(String(targetId));
	if (!localIds || localIds.size === 0) {
		return [];
	}
	const messages = [];
	const staleLocalIds = [];
	localIds.forEach((localId) => {
		const message = runtimeIndex.byLocalId.get(localId);
		if (!message) {
			staleLocalIds.push(localId);
			return;
		}
		messages.push(message);
	});
	staleLocalIds.forEach((localId) => localIds.delete(localId));
	if (localIds.size === 0) {
		runtimeIndex.quoteRefsByTargetId.delete(String(targetId));
	}
	return messages;
}

function resolveGroupChatSenderMessageRefs(chat, userId) {
	const chatKey = resolveGroupSenderChatKey(chat);
	if (!chatKey || userId === undefined || userId === null || userId === '') {
		return null;
	}
	const senderMessageLocalIds = resolveGroupChatSenderRefs(chatKey);
	if (!senderMessageLocalIds) {
		return null;
	}
	const senderId = String(userId);
	const localIds = senderMessageLocalIds.get(senderId);
	if (!localIds || localIds.size === 0) {
		return null;
	}
	return {
		chatKey,
		senderId,
		senderMessageLocalIds,
		localIds
	};
}

function shouldUseIndexedGroupChatSenderSync(chat, senderRefs) {
	if (!chat || !senderRefs) {
		return false;
	}
	const loadedMessageCount = Array.isArray(chat.messages) ? chat.messages.length : 0;
	if (loadedMessageCount === 0) {
		return false;
	}
	return senderRefs.localIds.size / loadedMessageCount < GROUP_CHAT_SENDER_INDEX_MAX_DENSITY;
}

function findGroupChatMessagesBySender(chat, userId, senderRefs = null) {
	const resolvedSenderRefs = senderRefs || resolveGroupChatSenderMessageRefs(chat, userId);
	if (!resolvedSenderRefs) {
		return [];
	}
	const runtimeIndex = resolveMessageRuntimeIndex(chat);
	if (!runtimeIndex || !(runtimeIndex.byLocalId instanceof Map)) {
		return [];
	}
	const messages = [];
	const staleLocalIds = [];
	resolvedSenderRefs.localIds.forEach((localId) => {
		const message = runtimeIndex.byLocalId.get(String(localId));
		if (!message) {
			staleLocalIds.push(String(localId));
			return;
		}
		messages.push(message);
	});
	staleLocalIds.forEach((localId) => resolvedSenderRefs.localIds.delete(localId));
	if (resolvedSenderRefs.localIds.size === 0) {
		resolvedSenderRefs.senderMessageLocalIds.delete(resolvedSenderRefs.senderId);
		if (resolvedSenderRefs.senderMessageLocalIds.size === 0) {
			groupChatSenderMessageLocalIds.delete(resolvedSenderRefs.chatKey);
		}
	}
	return messages;
}

function findMessageArrayIndex(chat, targetMessage) {
	if (!chat || !Array.isArray(chat.messages) || !targetMessage) {
		return -1;
	}
	for (let idx = chat.messages.length - 1; idx >= 0; idx--) {
		if (chat.messages[idx] === targetMessage) {
			return idx;
		}
	}
	return -1;
}

function createPendingHistoryIndex() {
	return {
		...createMessageRuntimeIndex(),
		dedupKeyToMessage: new Map()
	};
}

function indexPendingHistoryMessage(pendingIndex, chatInfo, message) {
	if (!pendingIndex || !message || message.type == MESSAGE_TYPE.TIP_TIME) {
		return null;
	}
	const runtimeState = captureMessageRuntimeState(chatInfo, message);
	if (!runtimeState) {
		return null;
	}
	if (runtimeState.id) {
		pendingIndex.byId.set(runtimeState.id, message);
	}
	if (runtimeState.tmpId && runtimeState.selfSend) {
		pendingIndex.byTmpId.set(runtimeState.tmpId, message);
	}
	if (runtimeState.localId) {
		pendingIndex.byLocalId.set(runtimeState.localId, message);
	}
	if (runtimeState.seqNo) {
		pendingIndex.bySeqNo.set(runtimeState.seqNo, message);
	}
	addQuoteRuntimeRef(pendingIndex, runtimeState.quoteTargetId, runtimeState.localId);
	resolveMessageDedupKeysFromState(runtimeState).forEach((key) => {
		pendingIndex.dedupKeyToMessage.set(key, message);
	});
	return runtimeState;
}

function unindexPendingHistoryMessage(pendingIndex, message, runtimeState = null) {
	if (!pendingIndex || !message || message.type == MESSAGE_TYPE.TIP_TIME) {
		return;
	}
	const resolvedState = runtimeState || captureMessageRuntimeState(null, message);
	if (!resolvedState) {
		return;
	}
	if (resolvedState.id && pendingIndex.byId.get(resolvedState.id) === message) {
		pendingIndex.byId.delete(resolvedState.id);
	}
	if (resolvedState.tmpId && resolvedState.selfSend &&
		pendingIndex.byTmpId.get(resolvedState.tmpId) === message) {
		pendingIndex.byTmpId.delete(resolvedState.tmpId);
	}
	if (resolvedState.localId && pendingIndex.byLocalId.get(resolvedState.localId) === message) {
		pendingIndex.byLocalId.delete(resolvedState.localId);
	}
	if (resolvedState.seqNo && pendingIndex.bySeqNo.get(resolvedState.seqNo) === message) {
		pendingIndex.bySeqNo.delete(resolvedState.seqNo);
	}
	removeQuoteRuntimeRef(pendingIndex, resolvedState.quoteTargetId, resolvedState.localId);
	resolveMessageDedupKeysFromState(resolvedState).forEach((key) => {
		if (pendingIndex.dedupKeyToMessage.get(key) === message) {
			pendingIndex.dedupKeyToMessage.delete(key);
		}
	});
}

function findPendingHistoryDuplicateMessage(pendingIndex, msgInfo) {
	if (!pendingIndex || !msgInfo) {
		return null;
	}
	const dedupKeys = resolveMessageDedupKeys(msgInfo);
	for (let idx = 0; idx < dedupKeys.length; idx++) {
		const target = pendingIndex.dedupKeyToMessage.get(dedupKeys[idx]);
		if (target) {
			return target;
		}
	}
	return null;
}

function findPendingHistoryMessage(pendingIndex, msgInfo = {}) {
	if (!pendingIndex) {
		return null;
	}
	if (msgInfo.id !== undefined && msgInfo.id !== null && msgInfo.id !== '') {
		return pendingIndex.byId.get(String(msgInfo.id)) || null;
	}
	if (msgInfo.localId !== undefined && msgInfo.localId !== null && msgInfo.localId !== '') {
		return pendingIndex.byLocalId.get(String(msgInfo.localId)) || null;
	}
	if (msgInfo.selfSend && msgInfo.tmpId !== undefined && msgInfo.tmpId !== null && msgInfo.tmpId !== '') {
		return pendingIndex.byTmpId.get(String(msgInfo.tmpId)) || null;
	}
	if (msgInfo.seqNo !== undefined && msgInfo.seqNo !== null && msgInfo.seqNo !== '') {
		return pendingIndex.bySeqNo.get(String(msgInfo.seqNo)) || null;
	}
	return null;
}

function findPendingHistoryQuoteRefs(pendingIndex, targetId) {
	if (!pendingIndex || targetId === undefined || targetId === null || targetId === '') {
		return [];
	}
	const localIds = pendingIndex.quoteRefsByTargetId.get(String(targetId));
	if (!localIds || localIds.size === 0) {
		return [];
	}
	const messages = [];
	const staleLocalIds = [];
	localIds.forEach((localId) => {
		const message = pendingIndex.byLocalId.get(localId);
		if (!message) {
			staleLocalIds.push(localId);
			return;
		}
		messages.push(message);
	});
	staleLocalIds.forEach((localId) => localIds.delete(localId));
	if (localIds.size === 0) {
		pendingIndex.quoteRefsByTargetId.delete(String(targetId));
	}
	return messages;
}

function buildIncrementalTimeTipSegment(messages = [], initialTimeTip = 0) {
	const segment = [];
	let lastTimeTip = Number(initialTimeTip || 0);
	messages.forEach((message) => {
		if (!message || message.type == MESSAGE_TYPE.TIP_TIME) {
			return;
		}
		if (!lastTimeTip || lastTimeTip < message.sendTime - 600 * 1000) {
			segment.push({
				sendTime: message.sendTime,
				type: MESSAGE_TYPE.TIP_TIME
			});
			lastTimeTip = message.sendTime;
		}
		segment.push(message);
	});
	return {
		messages: segment,
		lastTimeTip
	};
}

function resolveHistoryMergeInsertMode(chat, insertedMessages = []) {
	if (!Array.isArray(insertedMessages) || insertedMessages.length === 0) {
		return 'none';
	}
	const firstExistingMessage = findEarliestChatMessage(chat && chat.messages || []);
	const lastExistingMessage = findLatestChatMessage(chat && chat.messages || []);
	if (!firstExistingMessage || !lastExistingMessage) {
		return 'append';
	}
	const firstExistingSeq = resolveMessageSequence(firstExistingMessage);
	const lastExistingSeq = resolveMessageSequence(lastExistingMessage);
	const firstInsertedSeq = resolveMessageSequence(insertedMessages[0]);
	const lastInsertedSeq = resolveMessageSequence(insertedMessages[insertedMessages.length - 1]);
	if (lastInsertedSeq < firstExistingSeq) {
		return 'prepend';
	}
	if (firstInsertedSeq > lastExistingSeq) {
		return 'append';
	}
	return 'rebuild';
}

function prependHistoryMessages(chat, insertedMessages = []) {
	if (!chat || !Array.isArray(insertedMessages) || insertedMessages.length === 0) {
		return 0;
	}
	const segment = buildIncrementalTimeTipSegment(insertedMessages);
	if (!Array.isArray(chat.messages) || chat.messages.length === 0) {
		chat.messages = segment.messages;
		chat.lastTimeTip = segment.lastTimeTip;
		return segment.messages.length;
	}
	const firstExistingMessage = findEarliestChatMessage(chat.messages);
	const lastPrependedMessage = insertedMessages[insertedMessages.length - 1];
	const shouldKeepFirstTimeTip = !firstExistingMessage ||
		lastPrependedMessage.sendTime < firstExistingMessage.sendTime - 600 * 1000;
	const existingMessages = !shouldKeepFirstTimeTip && chat.messages[0] && chat.messages[0].type == MESSAGE_TYPE.TIP_TIME
		? chat.messages.slice(1)
		: chat.messages;
	chat.messages = segment.messages.concat(existingMessages);
	if (!chat.lastTimeTip) {
		chat.lastTimeTip = segment.lastTimeTip;
	}
	return segment.messages.length;
}

function appendHistoryMessages(chat, insertedMessages = []) {
	if (!chat || !Array.isArray(insertedMessages) || insertedMessages.length === 0) {
		return 0;
	}
	const segment = buildIncrementalTimeTipSegment(insertedMessages, chat.lastTimeTip);
	if (!Array.isArray(chat.messages) || chat.messages.length === 0) {
		chat.messages = segment.messages;
		chat.lastTimeTip = segment.lastTimeTip;
		return segment.messages.length;
	}
	chat.messages.push(...segment.messages);
	if (segment.lastTimeTip) {
		chat.lastTimeTip = segment.lastTimeTip;
	}
	return segment.messages.length;
}

function rebuildHistoryMessages(chat, insertedMessages = []) {
	const rebuilt = rebuildMessagesWithTimeTips(
		(chat && chat.messages || []).filter((message) => message && message.type != MESSAGE_TYPE.TIP_TIME)
			.concat(insertedMessages)
	);
	clearGroupChatSenderIndex(chat);
	clearMessageRuntimeIndex(chat);
	chat.messages = rebuilt.messages;
	chat.lastTimeTip = rebuilt.lastTimeTip;
	rebuildMessageRuntimeIndex(chat);
	rebuildGroupChatSenderIndex(chat);
}

function resolveDirtyMessagesForPersistence(chat, dirtyLocalIds) {
	if (!chat || !(dirtyLocalIds instanceof Set) || dirtyLocalIds.size === 0) {
		return [];
	}
	const runtimeIndex = resolveMessageRuntimeIndex(chat);
	if (!runtimeIndex || !(runtimeIndex.byLocalId instanceof Map)) {
		return (chat.messages || []).filter((message) => {
			if (!message || message.type == MESSAGE_TYPE.TIP_TIME) {
				return false;
			}
			return dirtyLocalIds.has(ensurePersistedLocalId(message, chat));
		});
	}
	const messages = [];
	const unresolvedLocalIds = [];
	dirtyLocalIds.forEach((localId) => {
		const message = runtimeIndex.byLocalId.get(String(localId));
		if (!message || message.type == MESSAGE_TYPE.TIP_TIME) {
			unresolvedLocalIds.push(localId);
			return;
		}
		messages.push(message);
	});
	if (unresolvedLocalIds.length === 0) {
		return messages;
	}
	const unresolvedSet = new Set(unresolvedLocalIds.map((localId) => String(localId)));
	return messages.concat((chat.messages || []).filter((message) => {
		if (!message || message.type == MESSAGE_TYPE.TIP_TIME) {
			return false;
		}
		return unresolvedSet.has(ensurePersistedLocalId(message, chat));
	}));
}

function upsertChatLookup(chat) {
	if (!chat) {
		return;
	}
	chatLookup.set(resolveChatLookupKey(chat.type, chat.targetId), chat);
}

function clearPendingSummaryChats() {
	pendingSummaryChatKeys = new Set();
}

function markChatSummaryDirty(chat) {
	if (!chat) {
		return;
	}
	pendingSummaryChatKeys.add(resolveChatLookupKey(chat.type, chat.targetId));
}

function markChatDirty(chat) {
	if (!chat) {
		return;
	}
	dirtyChatKeys.add(resolveChatLookupKey(chat.type, chat.targetId));
}

function clearDirtyMessages(chat) {
	if (!chat) {
		return;
	}
	dirtyMessageLocalIdsByChatKey.delete(resolveChatLookupKey(chat.type, chat.targetId));
}

function markMessageDirty(chat, message) {
	if (!chat || !message || message.type == MESSAGE_TYPE.TIP_TIME) {
		return false;
	}
	const localId = ensurePersistedLocalId(message, chat);
	if (!localId) {
		return false;
	}
	const chatKey = resolveChatLookupKey(chat.type, chat.targetId);
	let dirtyLocalIds = dirtyMessageLocalIdsByChatKey.get(chatKey);
	if (!dirtyLocalIds) {
		dirtyLocalIds = new Set();
		dirtyMessageLocalIdsByChatKey.set(chatKey, dirtyLocalIds);
	}
	dirtyLocalIds.add(localId);
	markChatDirty(chat);
	return true;
}

function markMessagesDirty(chat, messages = []) {
	if (!chat || !Array.isArray(messages)) {
		return;
	}
	messages.forEach((message) => markMessageDirty(chat, message));
}

function restoreDirtyMessages(dirtyMessagesByChatKey) {
	if (!(dirtyMessagesByChatKey instanceof Map)) {
		return;
	}
	dirtyMessagesByChatKey.forEach((localIds, chatKey) => {
		if (!localIds || localIds.size === 0) {
			return;
		}
		let targetLocalIds = dirtyMessageLocalIdsByChatKey.get(chatKey);
		if (!targetLocalIds) {
			targetLocalIds = new Set();
			dirtyMessageLocalIdsByChatKey.set(chatKey, targetLocalIds);
		}
		localIds.forEach((localId) => targetLocalIds.add(localId));
	});
}

function syncChatSummaryFromMessages(chat, options = {}) {
	if (!chat) {
		return false;
	}
	const markDirty = options.markDirty !== false;
	const latestMessage = findLatestChatMessage(chat.messages);
	const keepExistingSummary = !latestMessage && Boolean(chat.summaryOnly);
	const nextLastContent = latestMessage
		? messageUtil.previewContent(latestMessage)
		: (keepExistingSummary ? (chat.lastContent || '') : '');
	const nextLastSendTime = latestMessage && latestMessage.sendTime ? latestMessage.sendTime : (chat.lastSendTime || 0);
	const nextSendNickName = latestMessage
		? (latestMessage.sendNickName || '')
		: (keepExistingSummary ? (chat.sendNickName || '') : '');
	const nextLastMessageType = latestMessage
		? latestMessage.type
		: (keepExistingSummary ? (chat.lastMessageType ?? null) : null);
	const nextOptTime = latestMessage && latestMessage.sendTime ? latestMessage.sendTime : (chat.optTime || nextLastSendTime || 0);
	let changed = false;
	if (chat.lastContent !== nextLastContent) {
		chat.lastContent = nextLastContent;
		changed = true;
	}
	if (chat.lastSendTime !== nextLastSendTime) {
		chat.lastSendTime = nextLastSendTime;
		changed = true;
	}
	if (chat.sendNickName !== nextSendNickName) {
		chat.sendNickName = nextSendNickName;
		changed = true;
	}
	if (chat.lastMessageType !== nextLastMessageType) {
		chat.lastMessageType = nextLastMessageType;
		changed = true;
	}
	if (chat.optTime !== nextOptTime) {
		chat.optTime = nextOptTime;
		changed = true;
	}
	if (changed && markDirty) {
		markChatDirty(chat);
	}
	return changed;
}

function resolveGroupSenderDisplayName(groupId, userId) {
	if (!groupId || !userId) {
		return '';
	}
	const groupStore = useGroupStore();
	const friendStore = useFriendStore();
	const userStore = useUserStore();
	const member = groupStore.findGroupMember(groupId, userId);
	const friend = friendStore.findFriend(userId);
	const activeFriend = friend && !friend.deleted ? friend : null;
	const user = userStore.userInfo && userStore.userInfo.id == userId
		? userStore.userInfo
		: null;
	return resolveGroupMemberDisplayName(member, activeFriend, user);
}

function syncGroupChatSenderDisplay(chat, userId, displayName) {
	if (!chat || chat.type != 'GROUP' || !displayName) {
		return false;
	}
	let changed = false;
	const senderRefs = resolveGroupChatSenderMessageRefs(chat, userId);
	const candidateMessages = shouldUseIndexedGroupChatSenderSync(chat, senderRefs)
		? findGroupChatMessagesBySender(chat, userId, senderRefs)
		: (Array.isArray(chat.messages) ? chat.messages : []);
	candidateMessages.forEach((message) => {
		if (!message) {
			return;
		}
		let messageChanged = false;
		if (message.sendId == userId && message.sendNickName !== displayName) {
			message.sendNickName = displayName;
			messageChanged = true;
			changed = true;
		}
		if (message.quoteMessage && message.quoteMessage.sendId == userId &&
			message.quoteMessage.sendNickName !== displayName) {
			message.quoteMessage.sendNickName = displayName;
			messageChanged = true;
			changed = true;
		}
		if (messageChanged) {
			markMessageDirty(chat, message);
		}
	});
	const latestMessage = findLatestChatMessage(chat.messages);
	const nextSendNickName = latestMessage
		? (latestMessage.sendNickName || '')
		: (chat.summaryOnly ? (chat.sendNickName || '') : '');
	if (chat.sendNickName !== nextSendNickName) {
		chat.sendNickName = nextSendNickName;
		changed = true;
	}
	if (changed) {
		markChatDirty(chat);
	}
	return changed;
}


let lastWriteTime = 0;
let writeDiskTimer = null;
let persistQueue = Promise.resolve();
let pendingDeletedLocalIds = new Set();
let pendingClearedConversationKeys = new Set();
let dirtyConversationStateKeys = new Set();
let replaceConversationStatesPending = false;
let cursorConfigsDirty = false;
let pendingChatCompactionKeys = new Set();
let chatCompactionTimer = null;
let chatCompactionScheduler = '';

const INACTIVE_CHAT_RECENT_REAL_MESSAGE_LIMIT = 50;
const GROUP_CHAT_SENDER_INDEX_MAX_DENSITY = 0.4;

function enqueuePersist(task) {
	const run = persistQueue.then(() => Promise.resolve().then(task));
	persistQueue = run.then(() => undefined, () => undefined);
	return run;
}

const CHAT_CURSOR_CONFIG_KEYS = {
	privateMsgMaxId: 'chat:privateMsgMaxId',
	groupMsgMaxId: 'chat:groupMsgMaxId',
	systemMsgMaxSeqNo: 'chat:systemMsgMaxSeqNo'
};

function resolveChatRuntimeKey(chatInfo) {
	return resolveChatLookupKey(chatInfo && chatInfo.type, chatInfo && chatInfo.targetId);
}

function clearPendingChatCompactionState() {
	pendingChatCompactionKeys = new Set();
	if (chatCompactionTimer) {
		if (chatCompactionScheduler == 'idle' && typeof window !== 'undefined' &&
			typeof window.cancelIdleCallback == 'function') {
			window.cancelIdleCallback(chatCompactionTimer);
		} else {
			clearTimeout(chatCompactionTimer);
		}
		chatCompactionTimer = null;
		chatCompactionScheduler = '';
	}
}

function schedulePendingChatCompaction(chatStore) {
	if (!chatStore || chatCompactionTimer) {
		return;
	}
	const run = () => {
		chatCompactionTimer = null;
		chatCompactionScheduler = '';
		if (chatStore && typeof chatStore.flushPendingChatMessageCompaction == 'function') {
			chatStore.flushPendingChatMessageCompaction();
		}
	};
	if (typeof window !== 'undefined' && typeof window.requestIdleCallback == 'function') {
		chatCompactionScheduler = 'idle';
		chatCompactionTimer = window.requestIdleCallback(run, { timeout: 500 });
		return;
	}
	chatCompactionScheduler = 'timeout';
	chatCompactionTimer = setTimeout(run, 0);
}

function queuePendingChatCompaction(chatStore, chat) {
	const chatKey = resolveChatRuntimeKey(chat);
	if (!chatStore || !chatKey) {
		return false;
	}
	pendingChatCompactionKeys.add(chatKey);
	schedulePendingChatCompaction(chatStore);
	return true;
}

function compactChatMessagesForResidency(chat, limit = INACTIVE_CHAT_RECENT_REAL_MESSAGE_LIMIT) {
	if (!chat || !Array.isArray(chat.messages) || limit <= 0) {
		return false;
	}
	const recentMessages = [];
	let hasOverflow = false;
	for (let idx = chat.messages.length - 1; idx >= 0; idx--) {
		const message = chat.messages[idx];
		if (!message || message.type == MESSAGE_TYPE.TIP_TIME) {
			continue;
		}
		if (recentMessages.length < limit) {
			recentMessages.push(message);
			continue;
		}
		hasOverflow = true;
		break;
	}
	if (!hasOverflow) {
		return false;
	}
	recentMessages.reverse();
	const rebuilt = buildIncrementalTimeTipSegment(recentMessages);
	clearGroupChatSenderIndex(chat);
	clearMessageRuntimeIndex(chat);
	chat.messages = rebuilt.messages;
	chat.lastTimeTip = rebuilt.lastTimeTip;
	rebuildMessageRuntimeIndex(chat);
	rebuildGroupChatSenderIndex(chat);
	syncChatSummaryFromMessages(chat, { markDirty: false });
	return true;
}

function resolveConversationKey(chatInfo) {
	if (!chatInfo) {
		return '';
	}
	return `${chatInfo.type}-${chatInfo.targetId}`;
}

function normalizeConversationState(source) {
	if (!source || typeof source !== 'object') {
		return null;
	}
	const type = String(source.type || '').toUpperCase();
	const targetId = source.targetId;
	if (!type || targetId === undefined || targetId === null || targetId === '') {
		return null;
	}
	return {
		key: resolveConversationKey({ type, targetId }),
		type,
		targetId,
		isDnd: Boolean(source.isDnd),
		isTop: Boolean(source.isTop),
		readCursor: Math.max(0, Number(source.readCursor) || 0),
		peerReadCursor: Math.max(0, Number(source.peerReadCursor) || 0)
	};
}

function applyPeerReadStatus(message, state) {
	if (!message || !state || !message.selfSend || !message.id ||
		Number(message.id) > Number(state.peerReadCursor || 0) || message.status >= MESSAGE_STATUS.RECALL) {
		return false;
	}
	if (message.status === MESSAGE_STATUS.READED) {
		return false;
	}
	message.status = MESSAGE_STATUS.READED;
	return true;
}

function ensurePersistedLocalId(message, chatInfo) {
	return ensureStableMessageLocalId(message, chatInfo);
}

function toPersistedConversation(chat) {
	if (!chat) {
		return null;
	}
	return {
		key: resolveConversationKey(chat),
		type: chat.type,
		targetId: chat.targetId,
		showName: chat.showName || '',
		headImage: chat.headImage || '',
		userName: chat.userName || '',
		draftText: chat.draftText || '',
		lastContent: chat.lastContent || '',
		lastMessageType: chat.lastMessageType ?? null,
		lastSendTime: chat.lastSendTime || 0,
		sendNickName: chat.sendNickName || '',
		optTime: chat.optTime || 0,
		unreadCount: chat.unreadCount || 0,
		atMe: Boolean(chat.atMe),
		atAll: Boolean(chat.atAll),
		lastAtMessageId: chat.lastAtMessageId ?? -1,
		summaryOnly: Boolean(chat.summaryOnly),
		recentHistoryHydrated: Boolean(chat.recentHistoryHydrated),
		historyCutoffSeqNo: resolveMessageSequence({ seqNo: chat.historyCutoffSeqNo }),
		messageRemovalCutoffs: { ...(chat.messageRemovalCutoffs || {}) },
		delete: Boolean(chat.delete)
	};
}

function fromPersistedConversation(conversation) {
	if (!conversation || conversation.delete) {
		return null;
	}
	const historyCutoffSeqNo = resolveMessageSequence({ seqNo: conversation.historyCutoffSeqNo });
	const hasLegacyGroupRecallSummary = conversation.type == 'GROUP' &&
		conversation.lastMessageType == MESSAGE_TYPE.RECALL;
	return {
		targetId: conversation.targetId,
		type: conversation.type,
		showName: conversation.showName || '',
		headImage: conversation.headImage || '',
		userName: conversation.userName || '',
		searchText: '',
		draftText: typeof conversation.draftText === 'string' ? conversation.draftText : '',
		lastContent: hasLegacyGroupRecallSummary ? '' : (conversation.lastContent || ''),
		lastMessageType: hasLegacyGroupRecallSummary ? null : (conversation.lastMessageType ?? null),
		lastSendTime: conversation.lastSendTime || 0,
		sendNickName: conversation.sendNickName || '',
		optTime: conversation.optTime || conversation.lastSendTime || 0,
		unreadCount: conversation.unreadCount || 0,
		messages: [],
		summaryOnly: true,
		messageRemovalCutoffs: { ...(conversation.messageRemovalCutoffs || {}) },
		recentHistoryHydrated: Boolean(conversation.recentHistoryHydrated),
		historyCutoffSeqNo,
		atMe: Boolean(conversation.atMe),
		atAll: Boolean(conversation.atAll),
		lastAtMessageId: conversation.lastAtMessageId ?? -1
	};
}

function toPersistedMessages(chat, dirtyLocalIds = null) {
	if (!chat || !Array.isArray(chat.messages)) {
		return [];
	}
	const filterByDirty = dirtyLocalIds instanceof Set;
	if (filterByDirty && dirtyLocalIds.size === 0) {
		return [];
	}
	const messages = filterByDirty
		? resolveDirtyMessagesForPersistence(chat, dirtyLocalIds)
		: chat.messages;
	const sanitized = sanitizePersistedMessages(messages).messages;
	return sanitized
		.filter((message) => message && message.type != MESSAGE_TYPE.TIP_TIME)
		.map((message) => {
			ensurePersistedLocalId(message, chat);
			const seqNo = resolveMessageSequence(message);
			const persisted = {
				...message,
				convKey: resolveConversationKey(chat),
				localId: message.localId,
				seqNo
			};
			TRANSIENT_MESSAGE_KEYS.forEach((key) => {
				delete persisted[key];
			});
			return persisted;
		});
}

function toPersistedCursorConfigs(chatStore) {
	return [
		{ key: CHAT_CURSOR_CONFIG_KEYS.privateMsgMaxId, val: chatStore.privateMsgMaxId || 0 },
		{ key: CHAT_CURSOR_CONFIG_KEYS.groupMsgMaxId, val: chatStore.groupMsgMaxId || 0 },
		{ key: CHAT_CURSOR_CONFIG_KEYS.systemMsgMaxSeqNo, val: chatStore.systemMsgMaxSeqNo || 0 }
	];
}

function syncMessageCursor(chatStore, msgInfo, type) {
	if (!chatStore || !msgInfo || !type) {
		return false;
	}
	let changed = false;
	const messageId = Number(msgInfo.id);
	if (Number.isFinite(messageId) && messageId > 0) {
		if (type == "PRIVATE" && messageId > chatStore.privateMsgMaxId) {
			chatStore.privateMsgMaxId = messageId;
			changed = true;
		}
		if (type == "GROUP" && messageId > chatStore.groupMsgMaxId) {
			chatStore.groupMsgMaxId = messageId;
			changed = true;
		}
	}
	const seqNo = Number(msgInfo.seqNo);
	if (type == "SYSTEM" && Number.isFinite(seqNo) && seqNo > chatStore.systemMsgMaxSeqNo) {
		chatStore.systemMsgMaxSeqNo = seqNo;
		changed = true;
	}
	if (changed) {
		cursorConfigsDirty = true;
	}
	return changed;
}

export default defineStore('chatStore', {
	state: () => {
		return {
			activeChat: null,
			chats: [],
			conversationStates: new Map(),
			conversationStateVersion: 0,
			privateMsgMaxId: 0,
			groupMsgMaxId: 0,
			systemMsgMaxSeqNo: 0,
			loading: true,
			storageHydrated: false,
			storageChatCount: 0,
			totalUnreadCount: 0
		}
	},
	actions: {
		initChats(chatsData) {
			const chats = Array.isArray(chatsData.chats) ? chatsData.chats : [];
			chats.forEach(chat => { chat.messageRemovalCutoffs = { ...(chat.messageRemovalCutoffs || {}) }; });
			this.chats = chats;
			const friendStore = useFriendStore();
			this.chats.forEach((chat) => {
				chat.searchText = chat.searchText || '';
				if (removeLegacyCompanyName(chat)) {
					markChatDirty(chat);
				}
				prepareChatSearchText(chat, chat.type == 'PRIVATE' ? friendStore.findFriend(chat.targetId) : null);
			});
			this.activeChat = null;
			this.privateMsgMaxId = chatsData.privateMsgMaxId || 0;
			this.groupMsgMaxId = chatsData.groupMsgMaxId || 0;
			this.systemMsgMaxSeqNo = chatsData.systemMsgMaxSeqNo || 0;
			this.loading = true;
			this.storageHydrated = true;
			this.storageChatCount = this.chats.length;
			resetChatLookup(this.chats);
			resetGroupChatSenderIndex(this.chats);
			resetMessageRuntimeIndex(this.chats);
			this.totalUnreadCount = recalculateTotalUnreadCount(this.chats, this);
			clearPendingSummaryChats();
			clearPendingChatCompactionState();
			cursorConfigsDirty = false;
			dirtyChatKeys = new Set();
			dirtyMessageLocalIdsByChatKey = new Map();
		},
		initConversationStates(states) {
			this.conversationStates = new Map();
			(Array.isArray(states) ? states : []).forEach((row) => {
				const state = normalizeConversationState(row);
				if (state) {
					this.conversationStates.set(state.key, state);
				}
			});
			this.conversationStateVersion++;
		},
		ensureConversationState(chatInfo) {
			const normalized = normalizeConversationState(chatInfo);
			if (!normalized) {
				return null;
			}
			let state = this.conversationStates.get(normalized.key);
			if (!state) {
				state = normalized;
				this.conversationStates.set(state.key, state);
			}
			return state;
		},
			replaceConversationStates(rows) {
				const nextStates = new Map();
				(Array.isArray(rows) ? rows : []).forEach((row) => {
					const current = row ? this.conversationStates.get(resolveConversationKey(row)) : null;
					const state = normalizeConversationState({
						...row,
						peerReadCursor: current ? current.peerReadCursor : 0
					});
				if (state) {
					nextStates.set(state.key, state);
				}
			});
			this.conversationStates = nextStates;
			this.conversationStateVersion++;
			dirtyConversationStateKeys = new Set(nextStates.keys());
			replaceConversationStatesPending = true;
			this.totalUnreadCount = recalculateTotalUnreadCount(this.chats, this);
			sortChatsInPlace(this.chats, this);
			this.saveToStorageInTimer();
		},
		isDnd(chatInfo) {
			void this.conversationStateVersion;
			const state = chatInfo ? this.conversationStates.get(resolveConversationKey(chatInfo)) : null;
			return Boolean(state && state.isDnd);
		},
		isTop(chatInfo) {
			void this.conversationStateVersion;
			const state = chatInfo ? this.conversationStates.get(resolveConversationKey(chatInfo)) : null;
			return Boolean(state && state.isTop);
		},
		openChat(chatInfo) {
			let chats = this.findChats()
			let chat = this.findChat(chatInfo);
			let created = false;
			const friendStore = useFriendStore();
			// 创建会话
			if (chat == null) {
				chat = {
					targetId: chatInfo.targetId,
					type: chatInfo.type,
					showName: chatInfo.showName,
					headImage: chatInfo.headImage,
					userName: chatInfo.userName,
					searchText: "",
					// 草稿文本（用于会话列表提示），需要预置字段保证响应式
					draftText: "",
					lastContent: "",
					lastMessageType: null,
					lastSendTime: new Date().getTime(),
						optTime: new Date().getTime(),
						unreadCount: 0,
						messages: [],
					summaryOnly: false,
					messageRemovalCutoffs: {},
					recentHistoryHydrated: false,
					historyCutoffSeqNo: 0,
					atMe: false,
					atAll: false,
					lastAtMessageId: -1,
					delete: false
				};
				prepareChatSearchText(chat, chat.type == 'PRIVATE' ? friendStore.findFriend(chat.targetId) : null);
				chats.push(chat);
				upsertChatLookup(chat);
				markChatDirty(chat);
				created = true;
			} else if (applyChatBadgeMutation(this, chat, () => {
					const metaChanged = syncChatMeta(chat, chatInfo, ['showName', 'headImage', 'userName']);
				return removeLegacyCompanyName(chat) || metaChanged;
			})) {
				prepareChatSearchText(chat, chat.type == 'PRIVATE' ? friendStore.findFriend(chat.targetId) : null);
				markChatDirty(chat);
				if (!this.loading) {
					this.saveToStorage();
				}
			} else {
				prepareChatSearchText(chat, chat.type == 'PRIVATE' ? friendStore.findFriend(chat.targetId) : null);
			}
			if (created) {
				this.moveTop(chat);
			}
		},
		setActiveChat(chatInfo) {
			const nextActiveChat = chatInfo ? this.findChat(chatInfo) : null;
			const previousActiveChat = this.activeChat;
			if (previousActiveChat && (!nextActiveChat ||
				resolveChatRuntimeKey(previousActiveChat) !== resolveChatRuntimeKey(nextActiveChat))) {
				queuePendingChatCompaction(this, previousActiveChat);
			}
			this.activeChat = nextActiveChat;
		},
		flushPendingChatMessageCompaction() {
			const activeChatKey = resolveChatRuntimeKey(this.activeChat);
			Array.from(pendingChatCompactionKeys).forEach((chatKey) => {
				const chat = chatLookup.get(chatKey);
				if (!chat || chat.delete || !Array.isArray(chat.messages) || chat.messages.length === 0) {
					pendingChatCompactionKeys.delete(chatKey);
					return;
				}
				if (chatKey === activeChatKey) {
					return;
				}
				const dirtyLocalIds = dirtyMessageLocalIdsByChatKey.get(chatKey);
				if (dirtyLocalIds && dirtyLocalIds.size > 0) {
					return;
				}
				compactChatMessagesForResidency(chat);
				pendingChatCompactionKeys.delete(chatKey);
			});
		},
		markChatDirty(chatInfo) {
			const chat = this.findChat(chatInfo);
			if (!chat) {
				return false;
			}
			markChatDirty(chat);
			return true;
		},
		resetUnreadCount(chatInfo) {
			let chat = this.findChat(chatInfo);
			if (!chat) return;
			applyChatBadgeMutation(this, chat, () => {
				chat.unreadCount = 0;
			});
			markChatDirty(chat);
			this.saveToStorage();
		},
		resetAtMessage(chatInfo) {
			let chat = this.findChat(chatInfo);
			chat.atAll = false;
			chat.atMe = false;
			chat.lastAtMessageId = -1;
			markChatDirty(chat);
			this.saveToStorage();
		},
		readedMessage(pos) {
			let chat = this.findChatByFriend(pos.friendId);
			if (!chat) return;
			const cursor = Math.max(0, Number(pos.maxId) || 0);
			if (!cursor) return;
			const state = this.ensureConversationState(chat);
			if (!state || cursor <= state.peerReadCursor) return;
			state.peerReadCursor = cursor;
			dirtyConversationStateKeys.add(state.key);
			this.conversationStateVersion++;
			chat.messages.forEach(message => applyPeerReadStatus(message, state));
			this.saveToStorageInTimer();
		},
		restoreChatShell(chatInfo, summary = {}) {
			if (!chatInfo || !chatInfo.type || !chatInfo.targetId) {
				return null;
			}
			const chats = this.findChats();
			let chat = this.findChat(chatInfo);
			const lastMessage = summary.lastMessage || null;
			const hideGroupRecallSummary = chatInfo.type == 'GROUP' &&
				((lastMessage && lastMessage.type == MESSAGE_TYPE.RECALL) ||
					summary.lastMessageType == MESSAGE_TYPE.RECALL);
			const nextLastContent = hideGroupRecallSummary
				? ''
				: lastMessage
				? messageUtil.previewContent(lastMessage)
				: (summary.lastContent || '');
			const nextLastSendTime = lastMessage && lastMessage.sendTime
				? lastMessage.sendTime
				: (summary.lastSendTime || 0);
			const nextSendNickName = hideGroupRecallSummary
				? ''
				: lastMessage
				? (lastMessage.sendNickName || '')
				: (summary.sendNickName || '');
			const nextLastMessageType = hideGroupRecallSummary
				? null
				: lastMessage
				? lastMessage.type
				: (summary.lastMessageType ?? null);
			const nextOptTime = summary.optTime || nextLastSendTime || 0;
			const friendStore = useFriendStore();
			if (!chat) {
				chat = {
					targetId: chatInfo.targetId,
					type: chatInfo.type,
					showName: chatInfo.showName,
					headImage: chatInfo.headImage,
					userName: chatInfo.userName,
					searchText: "",
					draftText: "",
					lastContent: nextLastContent,
					lastMessageType: nextLastMessageType,
					lastSendTime: nextLastSendTime,
					sendNickName: nextSendNickName,
						optTime: nextOptTime,
						unreadCount: 0,
						messages: [],
					summaryOnly: true,
					messageRemovalCutoffs: {},
					recentHistoryHydrated: false,
					historyCutoffSeqNo: 0,
					atMe: false,
					atAll: false,
					lastAtMessageId: -1,
					delete: false
				};
				prepareChatSearchText(chat, chat.type == 'PRIVATE' ? friendStore.findFriend(chat.targetId) : null);
				chats.push(chat);
				upsertChatLookup(chat);
				markChatDirty(chat);
				return chat;
			}
			let changed = removeLegacyCompanyName(chat);
			changed = syncChatMeta(chat, chatInfo, ['showName', 'headImage', 'userName']) || changed;
			const hasMessages = Boolean(findLatestChatMessage(chat.messages));
			const forceSummary = summary.force === true;
			if (forceSummary || (!hasMessages && resolveMessageSequence({ seqNo: chat.historyCutoffSeqNo }) <= 0)) {
				if (chat.lastContent !== nextLastContent) {
					chat.lastContent = nextLastContent;
					changed = true;
				}
				if (chat.lastSendTime !== nextLastSendTime) {
					chat.lastSendTime = nextLastSendTime;
					changed = true;
				}
				if (chat.sendNickName !== nextSendNickName) {
					chat.sendNickName = nextSendNickName;
					changed = true;
				}
				if (chat.lastMessageType !== nextLastMessageType) {
					chat.lastMessageType = nextLastMessageType;
					changed = true;
				}
				const mergedOptTime = Math.max(Number(chat.optTime || 0), Number(nextOptTime || 0));
				if (chat.optTime !== mergedOptTime) {
					chat.optTime = mergedOptTime;
					changed = true;
				}
				if (!forceSummary && !chat.summaryOnly) {
					chat.summaryOnly = true;
					changed = true;
				}
			}
			if (changed) {
				markChatDirty(chat);
			}
			prepareChatSearchText(chat, chat.type == 'PRIVATE' ? friendStore.findFriend(chat.targetId) : null);
			return chat;
		},
		sortChats() {
			sortChatsInPlace(this.chats, this);
			resetChatLookup(this.chats);
		},
		cleanMessage(chatInfo) {
			let chat = this.findChat(chatInfo);
			const nextHistoryCutoffSeqNo = (chat.messages || []).reduce((maxSeqNo, message) => {
				if (!message || message.type == MESSAGE_TYPE.TIP_TIME) {
					return maxSeqNo;
				}
				return Math.max(maxSeqNo, resolveMessageSequence(message));
			}, resolveMessageSequence({ seqNo: chat.historyCutoffSeqNo }));
			chat.lastContent = '';
			chat.lastMessageType = null;
			applyChatBadgeMutation(this, chat, () => {
				chat.unreadCount = 0;
			});
			chat.summaryOnly = false;
			chat.recentHistoryHydrated = false;
			chat.historyCutoffSeqNo = nextHistoryCutoffSeqNo;
			chat.atMe = false;
			chat.atAll = false;
			chat.lastAtMessageId = -1
			markChatDirty(chat);
			clearDirtyMessages(chat);
			clearMessageRuntimeIndex(chat);
			chat.messages = [];
			clearGroupChatSenderIndex(chat);
			pendingClearedConversationKeys.add(resolveConversationKey(chatInfo));
			this.saveToStorage();
		},
		removeChat(chatInfo) {
			let chat = this.findChat(chatInfo);
			if (chat == this.activeChat) {
				this.activeChat = null;
			}
			applyChatBadgeMutation(this, chat, () => {
				chat.delete = true;
			});
			clearGroupChatSenderIndex(chat);
			clearDirtyMessages(chat);
			clearMessageRuntimeIndex(chat);
			markChatDirty(chat);
			this.saveToStorage()
		},
		removePrivateChat(friendId) {
			let chat = this.findChatByFriend(friendId);
			chat && this.removeChat(chat);
		},
		removeGroupChat(groupId) {
			let chat = this.findChatByGroup(groupId);
			chat && this.removeChat(chat);
		},
		moveTop(chatInfo) {
			if (this.loading) {
				return;
			}
			const chat = this.findChat(chatInfo);
			repositionChatInPlace(this.chats, chat, this);
		},
		insertMessage(msgInfo, chatInfo) {
			const currentChat = this.findChat(chatInfo);
			if (currentChat && currentChat.type == 'GROUP') {
				applyGroupMessageRemoval(msgInfo, currentChat.messageRemovalCutoffs);
				if (msgInfo.type == MESSAGE_TYPE.RECALL && msgInfo.recallTombstone) return;
			}
			let type = chatInfo.type;
			// 预置 id 字段，避免消息先入列后回填 id 时因新增属性导致非响应式
			if (msgInfo.id === undefined) {
				msgInfo.id = null;
			}
			ensurePersistedLocalId(msgInfo, chatInfo);
			syncMessageCursor(this, msgInfo, type);
			// 如果是已存在消息，则覆盖旧的消息数据
			let chat = this.findChat(chatInfo);
			if (!chat) {
				this.openChat(chatInfo);
				chat = this.findChat(chatInfo);
			}
			applyPeerReadStatus(msgInfo, this.conversationStates.get(resolveConversationKey(chatInfo)));
			let message = this.findMessage(chat, msgInfo);
			if (message) {
				const previousState = captureMessageRuntimeState(chat, message);
				unindexRuntimeMessage(chat, message, previousState);
				unindexGroupChatMessage(chat, previousState);
				mergeDuplicateMessage(message, msgInfo);
				indexRuntimeMessage(chat, message);
				indexGroupChatMessage(chat, message);
				markMessageDirty(chat, message);
				this.saveToStorageInTimer();
				return;
			}
			chat.summaryOnly = false;
			if (this.loading) {
				chat.lastSendTime = msgInfo.sendTime;
				chat.sendNickName = msgInfo.sendNickName;
				chat.lastMessageType = msgInfo.type;
				chat.optTime = msgInfo.sendTime;
				markChatSummaryDirty(chat);
			} else {
				// 会话列表内容
				chat.lastContent = messageUtil.previewContent(msgInfo);
				chat.lastSendTime = msgInfo.sendTime;
				chat.sendNickName = msgInfo.sendNickName;
				chat.lastMessageType = msgInfo.type;
				chat.optTime = msgInfo.sendTime;
			}
				// 未读加1
				if (!msgInfo.selfSend && msgInfo.status != MESSAGE_STATUS.READED &&
					msgInfo.status != MESSAGE_STATUS.RECALL && msgInfo.type != MESSAGE_TYPE.TIP_TEXT) {
					applyChatBadgeMutation(this, chat, () => {
						chat.unreadCount++;
					});
				}
			// 是否有人@我
			if (!msgInfo.selfSend && chat.type == "GROUP" && msgInfo.atUserIds &&
				msgInfo.status != MESSAGE_STATUS.READED) {
				const userStore = useUserStore();
				let userId = userStore.userInfo.id;
				if (msgInfo.atUserIds.indexOf(userId) >= 0) {
					chat.atMe = true;
					chat.lastAtMessageId = msgInfo.id;
				}
				if (msgInfo.atUserIds.indexOf(-1) >= 0) {
					chat.atAll = true;
					chat.lastAtMessageId = msgInfo.id;
				}
			}
			// 间隔大于10分钟插入时间显示
			if (!chat.lastTimeTip || (chat.lastTimeTip < msgInfo.sendTime - 600 * 1000)) {
				chat.messages.push({
					sendTime: msgInfo.sendTime,
					type: MESSAGE_TYPE.TIP_TIME,
				});
				chat.lastTimeTip = msgInfo.sendTime;
			}
			chat.messages.push(msgInfo);
			indexRuntimeMessage(chat, msgInfo);
			indexGroupChatMessage(chat, msgInfo);
			markMessageDirty(chat, msgInfo);
			this.moveTop(chat);
			this.saveToStorageInTimer();
		},
		mergeHistoryMessages(messages, chatInfo, options = {}) {
			if (!Array.isArray(messages) || messages.length === 0 || !chatInfo) {
				return 0;
			}
			const isLocalDbPage = options.source === 'local-db-page';
			const shouldPersistMutation = !isLocalDbPage;
			let chat = this.findChat(chatInfo);
			if (!chat) {
				this.openChat(chatInfo);
				chat = this.findChat(chatInfo);
			}
			if (!chat) {
				return 0;
			}
			messages.filter(message => message && message.type == MESSAGE_TYPE.GROUP_MEMBER_MESSAGE_CLEARED)
				.forEach(message => this.clearRemovedMemberMessages(message, chatInfo));
			messages = messages.filter(message => message && message.type != MESSAGE_TYPE.GROUP_MEMBER_MESSAGE_CLEARED);
			if (chat.type == 'GROUP') messages.forEach(message => applyGroupMessageRemoval(message, chat.messageRemovalCutoffs));
			const conversationState = this.conversationStates.get(resolveConversationKey(chat));
			messages.forEach(message => applyPeerReadStatus(message, conversationState));
			const sortedMessages = messages.slice().sort((a, b) => {
				const seqDiff = resolveMessageSequence(a) - resolveMessageSequence(b);
				if (seqDiff !== 0) {
					return seqDiff;
				}
				const sendTimeDiff = Number(a && a.sendTime || 0) - Number(b && b.sendTime || 0);
				if (sendTimeDiff !== 0) {
					return sendTimeDiff;
				}
				return Number(a && a.id || 0) - Number(b && b.id || 0);
			});
			sortedMessages.forEach((message) => normalizeStoredGroupRecall(message, chatInfo));
			const pendingHistoryIndex = createPendingHistoryIndex();
			const pendingInsertedMessages = [];
			const dirtyMergedMessages = new Set();
			let mutated = false;
			const markMergedMessageDirty = (message, shouldFlagMutation = false) => {
				if (shouldPersistMutation && message) {
					dirtyMergedMessages.add(message);
				}
				if (shouldFlagMutation) {
					mutated = true;
				}
			};
			const mutateIndexedHistoryMessage = (target, source, updatePendingIndex = false) => {
				if (!target || !source) {
					return false;
				}
				const previousState = captureMessageRuntimeState(chat, target);
				if (updatePendingIndex) {
					unindexPendingHistoryMessage(pendingHistoryIndex, target, previousState);
				} else {
					unindexRuntimeMessage(chat, target, previousState);
					unindexGroupChatMessage(chat, previousState);
				}
				source(target);
				if (updatePendingIndex) {
					indexPendingHistoryMessage(pendingHistoryIndex, chat, target);
				} else {
					indexRuntimeMessage(chat, target);
					indexGroupChatMessage(chat, target);
				}
				return true;
			};
			const mergeExistingMessage = (target, message, updatePendingIndex = false) => {
				if (!target || !message) {
					return false;
				}
				mutateIndexedHistoryMessage(target, () => {
					mergeDuplicateMessage(target, message);
				}, updatePendingIndex);
				if (shouldPersistMutation) {
					dirtyMergedMessages.add(target);
					mutated = true;
				}
				return false;
			};
			const findHistoryTargetMessage = (msgInfo) => {
				const loadedMessage = this.findMessage(chat, msgInfo);
				if (loadedMessage) {
					return {
						message: loadedMessage,
						pending: false
					};
				}
				const pendingMessage = findPendingHistoryMessage(pendingHistoryIndex, msgInfo);
				if (pendingMessage) {
					return {
						message: pendingMessage,
						pending: true
					};
				}
				return null;
			};
			const applyMutationMessage = (message) => {
				if (!message) {
					return false;
				}
				if (message.type == MESSAGE_TYPE.RECALL) {
					const recallContent = parseRecallContent(message);
					if (!recallContent) {
						return message.status != MESSAGE_STATUS.RECALL;
					}
					const silent = shouldSilenceRecall(recallContent, chatInfo);
					const nextQuoteContent = JSON.stringify({ key: 'tip.recall.quote' });
					const target = findHistoryTargetMessage({ id: recallContent.id });
					if (target && target.message) {
						const targetChanged = target.message.status != MESSAGE_STATUS.RECALL ||
							target.message.type != (silent ? MESSAGE_TYPE.RECALL : MESSAGE_TYPE.TIP_TEXT) ||
							target.message.content !== (silent ? '' : recallContent.tip);
						mutateIndexedHistoryMessage(target.message, () => {
							applyRecallTargetState(target.message, recallContent, silent);
							ensurePersistedLocalId(target.message, chatInfo);
						}, target.pending);
						if (targetChanged) {
							markMergedMessageDirty(target.message, true);
						}
					}
					const quoteRefs = findMessageQuoteRefs(chat, recallContent.id)
						.concat(findPendingHistoryQuoteRefs(pendingHistoryIndex, recallContent.id));
					quoteRefs.forEach((item) => {
						if (!item || !item.quoteMessage || item.quoteMessage.id != recallContent.id) {
							return;
						}
						if (item.quoteMessage.content === nextQuoteContent &&
							item.quoteMessage.status == MESSAGE_STATUS.RECALL &&
							item.quoteMessage.type == MESSAGE_TYPE.TIP_TEXT) {
							return;
						}
						item.quoteMessage.content = nextQuoteContent;
						item.quoteMessage.status = MESSAGE_STATUS.RECALL;
						item.quoteMessage.type = MESSAGE_TYPE.TIP_TEXT;
						markMergedMessageDirty(item, true);
					});
					return true;
				}
				if (message.type == MESSAGE_TYPE.MESSAGE_EDITED) {
					const target = findHistoryTargetMessage({ id: message.id });
					if (!target || !target.message) {
						return true;
					}
					mutateIndexedHistoryMessage(target.message, () => {
						const stableLocalId = target.message.localId;
						const stableSeqNo = resolveMessageSequence(target.message);
						const originType = target.message.type;
						Object.assign(target.message, message);
						if (stableLocalId) {
							target.message.localId = stableLocalId;
						} else {
							ensurePersistedLocalId(target.message, chatInfo);
						}
						target.message.type = originType;
						if (stableSeqNo > 0 && resolveMessageSequence(target.message) < stableSeqNo) {
							target.message.seqNo = stableSeqNo;
						}
					}, target.pending);
					markMergedMessageDirty(target.message, true);
					return true;
				}
				return false;
			};
			let insertedCount = 0;
			sortedMessages.forEach((message) => {
				if (applyMutationMessage(message)) {
					return;
				}
				if (!message || message.type == MESSAGE_TYPE.TIP_TIME || isTransientLocalMessage(message)) {
					return;
				}
				ensurePersistedLocalId(message, chatInfo);
				const existingMessage = this.findMessage(chat, message);
				if (existingMessage) {
					mergeExistingMessage(existingMessage, message);
					return;
				}
				const pendingMessage = findPendingHistoryDuplicateMessage(pendingHistoryIndex, message);
				if (pendingMessage) {
					mergeExistingMessage(pendingMessage, message, true);
					return;
				}
				pendingInsertedMessages.push(message);
				indexPendingHistoryMessage(pendingHistoryIndex, chat, message);
				insertedCount++;
			});
			if (insertedCount === 0 && !mutated) {
				return 0;
			}
			const insertMode = resolveHistoryMergeInsertMode(chat, pendingInsertedMessages);
			if (insertMode == 'prepend') {
				prependHistoryMessages(chat, pendingInsertedMessages);
				pendingInsertedMessages.forEach((message) => {
					indexRuntimeMessage(chat, message);
					indexGroupChatMessage(chat, message);
				});
			} else if (insertMode == 'append') {
				appendHistoryMessages(chat, pendingInsertedMessages);
				pendingInsertedMessages.forEach((message) => {
					indexRuntimeMessage(chat, message);
					indexGroupChatMessage(chat, message);
				});
			} else if (insertMode == 'rebuild') {
				rebuildHistoryMessages(chat, pendingInsertedMessages);
			}
			chat.summaryOnly = false;
			syncChatSummaryFromMessages(chat, { markDirty: shouldPersistMutation });
			if (shouldPersistMutation) {
				pendingInsertedMessages.forEach((message) => dirtyMergedMessages.add(message));
				markMessagesDirty(chat, Array.from(dirtyMergedMessages));
				this.saveToStorageInTimer();
			}
			return insertedCount;
		},
		updateMessage(msgInfo, chatInfo) {
			// 获取对方id或群id
			let chat = this.findChat(chatInfo);
			let message = this.findMessage(chat, msgInfo);
			if (chat && chat.type == 'GROUP') {
				applyGroupMessageRemoval(msgInfo, chat.messageRemovalCutoffs);
				if (message && message.recallTombstone) return;
			}
			if (message) {
				const previousState = captureMessageRuntimeState(chat, message);
				const isEdited = msgInfo.type == MESSAGE_TYPE.MESSAGE_EDITED;
				const originType = message.type;
				const stableLocalId = message.localId;
				const stableSeqNo = resolveMessageSequence(message);
				const stableMessageId = Number(message.id);
				const stableLocalPreviewContent = message.localPreviewContent;
				unindexRuntimeMessage(chat, message, previousState);
				unindexGroupChatMessage(chat, previousState);
				// 属性拷贝
				Object.assign(message, msgInfo);
				if (stableLocalId) {
					message.localId = stableLocalId;
				} else {
					ensurePersistedLocalId(message, chatInfo);
				}
				if (stableLocalPreviewContent && !message.localPreviewContent) {
					message.localPreviewContent = stableLocalPreviewContent;
				}
				const nextMessageId = Number(message.id);
				if (stableMessageId > 0 && nextMessageId === stableMessageId &&
					stableSeqNo > 0 && resolveMessageSequence(message) < stableSeqNo) {
					message.seqNo = stableSeqNo;
				}
				syncMessageCursor(this, message, chatInfo.type);
				if (isEdited) {
					// 编辑消息仅更新内容，不改变原消息类型
					message.type = originType;
				}
				indexRuntimeMessage(chat, message);
				indexGroupChatMessage(chat, message);
				syncChatSummaryFromMessages(chat);
				markMessageDirty(chat, message);
				this.saveToStorage();
			}
		},
		deleteMessage(msgInfo, chatInfo) {
			return this.deleteMessages([msgInfo], chatInfo);
		},
		deleteMessages(messageInfos, chatInfo) {
			let chat = this.findChat(chatInfo);
			if (!chat) return 0;
			let maxDeletedSequence = 0;
			let deletedCount = 0;
			(messageInfos || []).forEach((msgInfo) => {
				const deletedMessage = this.findMessage(chat, msgInfo);
				const delIdx = findMessageArrayIndex(chat, deletedMessage);
				if (delIdx < 0) return;
				const runtimeState = captureMessageRuntimeState(chat, deletedMessage);
				ensurePersistedLocalId(deletedMessage, chatInfo);
				pendingDeletedLocalIds.add(deletedMessage.localId);
				unindexRuntimeMessage(chat, deletedMessage, runtimeState);
				unindexGroupChatMessage(chat, runtimeState);
				chat.messages.splice(delIdx, 1);
				maxDeletedSequence = Math.max(maxDeletedSequence, resolveMessageSequence(deletedMessage));
				deletedCount++;
			});
			if (!deletedCount) return 0;
			// Removing messages can invalidate neighbouring tips. Rebuild the
			// resident timeline once for the entire user operation.
			rebuildHistoryMessages(chat);
			const hasRemainingMessages = chat.messages.some((message) => message && message.type != MESSAGE_TYPE.TIP_TIME);
			if (!hasRemainingMessages) {
				chat.historyCutoffSeqNo = Math.max(
					resolveMessageSequence({ seqNo: chat.historyCutoffSeqNo }),
					maxDeletedSequence
				);
			}
			markChatDirty(chat);
			this.saveToStorage();
			return deletedCount;
		},
		clearRemovedMemberMessages(msgInfo, chatInfo) {
			const chat = this.findChat(chatInfo);
			if (!chat || chat.type != 'GROUP') return;
			let payload;
			try { payload = JSON.parse(msgInfo.content); } catch (_) { return; }
			const cutoffs = mergeMessageRemovalCutoffs(chat.messageRemovalCutoffs, payload);
			if (JSON.stringify(cutoffs) === JSON.stringify(chat.messageRemovalCutoffs || {})) return;
			chat.messageRemovalCutoffs = cutoffs;
			let removedUnread = 0;
			let removedAt = false;
			(chat.messages || []).forEach(message => {
				const unread = !message.selfSend && message.status != MESSAGE_STATUS.READED;
				if (!applyGroupMessageRemoval(message, cutoffs)) return;
				if (message.recallTombstone && unread) removedUnread++;
				if (message.recallTombstone && message.id == chat.lastAtMessageId) removedAt = true;
				markMessageDirty(chat, message);
			});
			clearGroupChatSenderIndex(chat);
			clearMessageRuntimeIndex(chat);
			rebuildHistoryMessages(chat);
			applyChatBadgeMutation(this, chat, () => {
				chat.unreadCount = Math.max(0, chat.unreadCount - removedUnread);
			});
			if (chat.summaryOnly) {
				chat.lastContent = '';
				chat.lastMessageType = null;
				chat.sendNickName = '';
			} else syncChatSummaryFromMessages(chat);
			if (removedAt) {
				chat.atMe = false;
				chat.atAll = false;
				chat.lastAtMessageId = -1;
			}
			markChatDirty(chat);
			this.saveToStorage();
		},
		recallMessage(msgInfo, chatInfo) {
			let chat = this.findChat(chatInfo);
			if (!chat) return;
			// 要撤回的消息id
			const recallContent = parseRecallContent(msgInfo);
			if (!recallContent) return;
			let id = recallContent.id;
			const silent = shouldSilenceRecall(recallContent, chatInfo);
			let changed = false;
			const targetMessage = this.findMessage(chat, { id });
			if (targetMessage) {
				applyRecallTargetState(targetMessage, recallContent, silent);
				ensurePersistedLocalId(targetMessage, chatInfo);
				markMessageDirty(chat, targetMessage);
				if (silent) {
					syncChatSummaryFromMessages(chat);
				} else {
					chat.lastContent = messageUtil.previewContent(targetMessage);
					chat.lastSendTime = msgInfo.sendTime;
					chat.sendNickName = '';
					chat.lastMessageType = MESSAGE_TYPE.TIP_TEXT;
					if (!msgInfo.selfSend && msgInfo.status != MESSAGE_STATUS.READED) {
						applyChatBadgeMutation(this, chat, () => {
							chat.unreadCount++;
						});
					}
				}
				changed = true;
			} else if (silent && chat.summaryOnly) {
				chat.lastContent = '';
				chat.lastMessageType = null;
				chat.sendNickName = '';
				changed = true;
			}
			findMessageQuoteRefs(chat, id).forEach((m) => {
				let messageChanged = false;
				if (m.quoteMessage && m.quoteMessage.id == id) {
					m.quoteMessage.content = JSON.stringify({ key: 'tip.recall.quote' });
					m.quoteMessage.status = MESSAGE_STATUS.RECALL;
					m.quoteMessage.type = MESSAGE_TYPE.TIP_TEXT
					messageChanged = true;
				}
				if (messageChanged) {
					markMessageDirty(chat, m);
					changed = true;
				}
			});
			if (changed) {
				if (silent) {
					syncChatSummaryFromMessages(chat);
				}
				markChatDirty(chat);
				this.saveToStorage();
			}
		},
		syncGroupChatMemberIdentity(userId, groupId = null) {
			if (!userId) {
				return false;
			}
			let changed = false;
			const syncChat = (chat) => {
				if (!chat || chat.type != 'GROUP') {
					return;
				}
				const nextDisplayName = resolveGroupSenderDisplayName(chat.targetId, userId);
				if (!nextDisplayName) {
					return;
				}
				if (syncGroupChatSenderDisplay(chat, userId, nextDisplayName)) {
					changed = true;
				}
			};
			if (groupId) {
				syncChat(this.findChatByGroup(groupId));
				return changed;
			}
			const chatKeys = groupSenderChatKeys.get(String(userId));
			if (!chatKeys || chatKeys.size === 0) {
				return false;
			}
			Array.from(chatKeys).forEach((chatKey) => {
				const chat = chatLookup.get(chatKey);
				if (!chat || chat.delete || chat.type != 'GROUP') {
					chatKeys.delete(chatKey);
					return;
				}
				syncChat(chat);
			});
			return changed;
		},
		updateChatFromFriend(friend) {
			let chat = this.findChatByFriend(friend.id);
			const nextShowName = resolvePrivateDisplayName(friend);
			let changed = false;
			// 更新会话中的昵称和头像
			if (chat) {
				const metaChanged = syncChatMeta(chat, {
					headImage: friend.headImage,
					showName: nextShowName,
					userName: friend.userName || ''
				}, ['headImage', 'showName', 'userName']);
				if (removeLegacyCompanyName(chat) || metaChanged) {
					prepareChatSearchText(chat, friend);
					markChatDirty(chat);
					changed = true;
				} else {
					prepareChatSearchText(chat, friend);
				}
			}
			if (this.syncGroupChatMemberIdentity(friend.id)) {
				changed = true;
			}
			if (changed) {
				this.saveToStorage();
			}
		},
		updateChatFromUser(user) {
			let chat = this.findChatByFriend(user.id);
			const nextHeadImage = user.headImageThumb || user.headImage || '';
			const friendStore = useFriendStore();
			const friend = friendStore.findFriend(user.id);
			const nextProfile = friend && !friend.deleted ? friend : user;
			const nextShowName = resolvePrivateDisplayName(nextProfile);
			let changed = false;
			// 更新会话中的昵称和头像
			if (chat) {
				const metaChanged = syncChatMeta(chat, {
					headImage: nextHeadImage,
					showName: nextShowName,
					userName: user.userName || ''
				}, ['headImage', 'showName', 'userName']);
				if (removeLegacyCompanyName(chat) || metaChanged) {
					prepareChatSearchText(chat, nextProfile);
					markChatDirty(chat);
					changed = true;
				} else {
					prepareChatSearchText(chat, nextProfile);
				}
			}
			if (this.syncGroupChatMemberIdentity(user.id)) {
				changed = true;
			}
			if (changed) {
				this.saveToStorage();
			}
		},
		updateChatFromGroup(group) {
			let chat = this.findChatByGroup(group.id);
			const nextShowName = resolveUsableGroupChatDisplayName(group);
			const nextHeadImage = group.headImageThumb || group.headImage || '';
			const nextMeta = {
				headImage: nextHeadImage
			};
			if (nextShowName) {
				nextMeta.showName = nextShowName;
			}
			let changed = false;
			if (chat && syncChatMeta(chat, nextMeta, ['headImage', 'showName'])) {
				prepareChatSearchText(chat);
				markChatDirty(chat);
				changed = true;
			} else if (chat) {
				prepareChatSearchText(chat);
			}
			const userStore = useUserStore();
			const currentUserId = userStore.userInfo && userStore.userInfo.id;
			if (this.syncGroupChatMemberIdentity(currentUserId, group.id)) {
				changed = true;
			}
			if (changed) {
				this.saveToStorage();
			}
		},
		setLoading(loading) {
			const previousLoading = this.loading;
			this.loading = loading;
			if (previousLoading && !loading && pendingSummaryChatKeys.size > 0) {
				pendingSummaryChatKeys.forEach((key) => {
					syncChatSummaryFromMessages(chatLookup.get(key));
				});
				clearPendingSummaryChats();
			}
		},
		advanceSyncCursor(stream, watermark) {
			const value = Number(watermark);
			if (!Number.isFinite(value) || value < 0) {
				throw new Error(`无效的 ${stream} 消息同步水位`);
			}
			if (stream === 'private') {
				this.privateMsgMaxId = Math.max(this.privateMsgMaxId, value);
			} else if (stream === 'group') {
				this.groupMsgMaxId = Math.max(this.groupMsgMaxId, value);
			} else if (stream === 'system') {
				this.systemMsgMaxSeqNo = Math.max(this.systemMsgMaxSeqNo, value);
			} else {
				throw new Error(`未知消息同步流: ${stream}`);
			}
			cursorConfigsDirty = true;
		},
		setDnd(chatInfo, isDnd) {
			const state = this.ensureConversationState(chatInfo);
			const nextValue = Boolean(isDnd);
			if (!state || state.isDnd === nextValue) {
				return;
			}
			state.isDnd = nextValue;
			dirtyConversationStateKeys.add(state.key);
			this.conversationStateVersion++;
			this.totalUnreadCount = recalculateTotalUnreadCount(this.chats, this);
			this.saveToStorageInTimer();
		},
		advanceReadCursor(chatInfo, messageId) {
			const cursor = Math.max(0, Number(messageId) || 0);
			if (!cursor) return;
			const state = this.ensureConversationState(chatInfo);
			if (!state || cursor <= state.readCursor) return;
			state.readCursor = cursor;
			dirtyConversationStateKeys.add(state.key);
			this.conversationStateVersion++;
			this.saveToStorageInTimer();
		},
		setTop(chatInfo, isTop) {
			const state = this.ensureConversationState(chatInfo);
			const nextValue = Boolean(isTop);
			if (!state || state.isTop === nextValue) {
				return;
			}
			state.isTop = nextValue;
			dirtyConversationStateKeys.add(state.key);
			this.conversationStateVersion++;
			this.moveTop(chatInfo);
			this.saveToStorageInTimer();
		},
		refreshAllLastContent() {
			this.chats.forEach(chat => this.refreshLastContent(chat));
		},
		refreshLastContent(chatInfo) {
			let chat = this.findChat(chatInfo);
			if (!chat) {
				return;
			}
			syncChatSummaryFromMessages(chat);
		},
		refreshChats() {
			const friendStore = useFriendStore();
			const groupStore = useGroupStore();
			this.chats.forEach(chat => {
				if (typeof chat.draftText !== 'string') {
					chat.draftText = chat.draftText ? String(chat.draftText) : "";
				}
				if (!chat.searchText) {
					chat.searchText = "";
				}
			})
			// 关系快照只刷新身份资料；会话设置由 chatStore 独占。
			this.chats.forEach(chat => {
				if (chat.type == 'PRIVATE') {
					let friend = friendStore.findFriend(chat.targetId);
					if (friend) {
						const metaChanged = syncChatMeta(chat, {
							userName: friend.userName || '',
							showName: resolvePrivateDisplayName(friend) || chat.showName || '',
							headImage: friend.headImage || chat.headImage || ''
						}, ['userName', 'showName', 'headImage']);
						if (removeLegacyCompanyName(chat) || metaChanged) {
							prepareChatSearchText(chat, friend);
							markChatDirty(chat);
						} else {
							prepareChatSearchText(chat, friend);
						}
					} else {
						if (removeLegacyCompanyName(chat)) {
							markChatDirty(chat);
						}
						prepareChatSearchText(chat, friend);
					}
				} else if (chat.type == 'GROUP') {
					let group = groupStore.findGroup(chat.targetId);
					if (group) {
						const nextShowName = resolveUsableGroupChatDisplayName(group) || chat.showName;
						const nextHeadImage = group.headImageThumb || chat.headImage;
						if (chat.showName !== nextShowName || chat.headImage !== nextHeadImage) {
							chat.showName = nextShowName;
							chat.headImage = nextHeadImage;
							prepareChatSearchText(chat);
							markChatDirty(chat);
						} else {
							prepareChatSearchText(chat);
						}
					} else {
						prepareChatSearchText(chat);
					}
				} else {
					prepareChatSearchText(chat);
				}
				syncChatSummaryFromMessages(chat);
			})
			this.totalUnreadCount = recalculateTotalUnreadCount(this.chats, this);
			// 排序
			sortChatsInPlace(this.chats, this);
			resetChatLookup(this.chats);
			resetGroupChatSenderIndex(this.chats);
			clearPendingSummaryChats();
			this.saveToStorage();
		},
		setDraftText(chatInfo, draftText) {
			let chat = this.findChat(chatInfo);
			if (!chat) return;
			chat.draftText = draftText || "";
			markChatDirty(chat);
			// 草稿属于用户输入态数据，切换会话时落盘，便于刷新后仍可提示
			this.saveToStorage();
		},
		saveToStorageInTimer() {
			if (!hasPendingPersistenceMutations()) {
				return;
			}
			// 控制每秒只能写入一次磁盘，避免消息量大时频繁写入造成卡顿
			const now = new Date().getTime();
			if (now - lastWriteTime >= 1000) {
				const saved = this.saveToStorage();
				if (saved) {
					lastWriteTime = now;
					clearTimeout(writeDiskTimer)
					writeDiskTimer = null;
				}
			} else if (!writeDiskTimer) {
				writeDiskTimer = setTimeout(() => {
					writeDiskTimer = null;
					this.saveToStorageInTimer();
				}, 1000)
			}
		},
		saveToStorage(force = false) {
			if (this.loading && !force) {
				return false;
			}
			const dirtyKeys = Array.from(dirtyChatKeys);
			const changedChats = dirtyKeys
				.map((key) => chatLookup.get(key))
				.filter(Boolean);
			const deletedLocalIds = Array.from(pendingDeletedLocalIds);
			const clearedConversationKeys = Array.from(pendingClearedConversationKeys);
			const stateKeys = Array.from(dirtyConversationStateKeys);
			const replaceConversationStates = replaceConversationStatesPending;
			const conversationStates = stateKeys
				.map((key) => this.conversationStates.get(key))
				.filter(Boolean)
				.map((state) => ({ ...state }));
			const shouldPersistCursorConfigs = cursorConfigsDirty;
			const cursorConfigs = shouldPersistCursorConfigs ? toPersistedCursorConfigs(this) : [];
			if (!changedChats.length && !deletedLocalIds.length && !clearedConversationKeys.length && !conversationStates.length &&
				!replaceConversationStates &&
				!shouldPersistCursorConfigs) {
				return false;
			}
			const pendingChats = changedChats.slice();
			const dirtyMessagesByChatKey = new Map();
			const pendingSnapshots = pendingChats.map((chat) => {
				const chatKey = resolveChatLookupKey(chat.type, chat.targetId);
				const dirtyLocalIds = new Set(dirtyMessageLocalIdsByChatKey.get(chatKey) || []);
				dirtyMessagesByChatKey.set(chatKey, dirtyLocalIds);
				dirtyMessageLocalIdsByChatKey.delete(chatKey);
				return {
					chat,
					convKey: resolveConversationKey(chat),
					delete: Boolean(chat.delete),
					conversation: toPersistedConversation(chat),
					messages: chat.delete ? [] : toPersistedMessages(chat, dirtyLocalIds)
				};
			});
			dirtyChatKeys = new Set();
			dirtyConversationStateKeys = new Set();
			replaceConversationStatesPending = false;
			pendingDeletedLocalIds = new Set();
			pendingClearedConversationKeys = new Set();
			cursorConfigsDirty = false;
			const persistRun = enqueuePersist(async () => {
				const db = getDB();
				try {
					await db.persistChatMutations({
						clearedConversationKeys,
						deletedLocalIds,
						deletedConversationKeys: pendingSnapshots
							.filter((snapshot) => snapshot.delete)
							.map((snapshot) => snapshot.convKey),
						snapshots: pendingSnapshots
								.filter((snapshot) => !snapshot.delete)
								.map((snapshot) => ({
									conversation: snapshot.conversation,
									messages: snapshot.messages
								})),
							conversationStates,
							replaceConversationStates,
							configs: cursorConfigs
						});
				} catch (error) {
					pendingChats.forEach((chat) => {
						markChatDirty(chat);
						});
						deletedLocalIds.forEach((localId) => pendingDeletedLocalIds.add(localId));
						clearedConversationKeys.forEach((convKey) => pendingClearedConversationKeys.add(convKey));
					stateKeys.forEach((key) => dirtyConversationStateKeys.add(key));
					replaceConversationStatesPending = replaceConversationStatesPending || replaceConversationStates;
					if (shouldPersistCursorConfigs) {
						cursorConfigsDirty = true;
					}
					restoreDirtyMessages(dirtyMessagesByChatKey);
					throw error;
				}
			});
			persistRun.then(() => {
				if (pendingChats.some(chat => chat.delete)) {
					this.chats = this.chats.filter(chat => !chat.delete);
					resetChatLookup(this.chats);
				}
				this.flushPendingChatMessageCompaction();
			}).catch((error) => {
				console.error('本地消息持久化失败', error);
			});
			return persistRun;
		},
		async flushPersistence() {
			clearTimeout(writeDiskTimer);
			writeDiskTimer = null;
			const run = this.saveToStorage(true);
			if (run) {
				await run;
			}
			await persistQueue;
		},
		async clearPersistedStorage() {
			clearTimeout(writeDiskTimer);
				writeDiskTimer = null;
				lastWriteTime = 0;
				pendingDeletedLocalIds = new Set();
				pendingClearedConversationKeys = new Set();
				dirtyConversationStateKeys = new Set();
				replaceConversationStatesPending = false;
				cursorConfigsDirty = false;
				clearPendingChatCompactionState();
			dirtyChatKeys = new Set();
			dirtyMessageLocalIdsByChatKey = new Map();
			await persistQueue.catch(() => undefined);
			persistQueue = Promise.resolve();
			const db = getDB();
			await db.clearLocalCache();
		},
		clear() {
			clearTimeout(writeDiskTimer);
				writeDiskTimer = null;
				lastWriteTime = 0;
				pendingDeletedLocalIds = new Set();
				pendingClearedConversationKeys = new Set();
				dirtyConversationStateKeys = new Set();
				replaceConversationStatesPending = false;
				cursorConfigsDirty = false;
				clearPendingChatCompactionState();
				dirtyChatKeys = new Set();
				dirtyMessageLocalIdsByChatKey = new Map();
				resetChatLookup([]);
				resetGroupChatSenderIndex([]);
				resetMessageRuntimeIndex([]);
				clearPendingSummaryChats();
				this.activeChat = null;
				this.chats = [];
				this.conversationStates = new Map();
				this.conversationStateVersion++;
				this.privateMsgMaxId = 0;
				this.groupMsgMaxId = 0;
				this.systemMsgMaxSeqNo = 0;
				this.loading = false;
				this.storageHydrated = false;
				this.storageChatCount = 0;
				this.totalUnreadCount = 0;
			},
			async loadChat() {
				const userStore = useUserStore();
				const userId = userStore.userInfo && userStore.userInfo.id;
				if (!userId) {
					this.storageHydrated = true;
					this.storageChatCount = 0;
					this.chats = [];
					this.conversationStates = new Map();
					this.conversationStateVersion++;
					resetChatLookup([]);
					resetGroupChatSenderIndex([]);
					resetMessageRuntimeIndex([]);
					clearPendingSummaryChats();
					clearPendingChatCompactionState();
					cursorConfigsDirty = false;
					dirtyConversationStateKeys = new Set();
					replaceConversationStatesPending = false;
					dirtyChatKeys = new Set();
					dirtyMessageLocalIdsByChatKey = new Map();
					this.totalUnreadCount = 0;
					return;
				}
				const db = getDB();
				const [conversations, conversationStates, privateMsgMaxId, groupMsgMaxId, systemMsgMaxSeqNo] = await Promise.all([
					db.loadAllConversations(),
					db.loadAllConversationStates(),
					db.findConfig(CHAT_CURSOR_CONFIG_KEYS.privateMsgMaxId),
					db.findConfig(CHAT_CURSOR_CONFIG_KEYS.groupMsgMaxId),
					db.findConfig(CHAT_CURSOR_CONFIG_KEYS.systemMsgMaxSeqNo)
				]);
			const chats = Array.isArray(conversations) ? conversations.map(fromPersistedConversation).filter(Boolean) : [];
			const friendStore = useFriendStore();
			chats.forEach((chat) => prepareChatSearchText(chat, chat.type == 'PRIVATE' ? friendStore.findFriend(chat.targetId) : null));
				this.initConversationStates(conversationStates);
				sortChatsInPlace(chats, this);
			this.chats = chats;
			this.privateMsgMaxId = Number(privateMsgMaxId) || 0;
			this.groupMsgMaxId = Number(groupMsgMaxId) || 0;
			this.systemMsgMaxSeqNo = Number(systemMsgMaxSeqNo) || 0;
			this.loading = true;
			this.storageHydrated = true;
			this.storageChatCount = chats.length;
				this.activeChat = null;
				resetChatLookup(this.chats);
				resetGroupChatSenderIndex(this.chats);
				resetMessageRuntimeIndex(this.chats);
				this.totalUnreadCount = recalculateTotalUnreadCount(this.chats, this);
				clearPendingSummaryChats();
				clearPendingChatCompactionState();
				cursorConfigsDirty = false;
				dirtyConversationStateKeys = new Set();
				replaceConversationStatesPending = false;
				dirtyChatKeys = new Set();
				dirtyMessageLocalIdsByChatKey = new Map();
			}
	},
	getters: {
		findChats: (state) => () => {
			return state.chats;
		},
		findChatIdx: (state) => (chat) => {
			let chats = state.findChats();
			const targetChat = chatLookup.get(resolveChatLookupKey(chat && chat.type, chat && chat.targetId));
			return targetChat ? chats.indexOf(targetChat) : -1;
		},
		findChat: () => (chat) => {
			return chatLookup.get(resolveChatLookupKey(chat && chat.type, chat && chat.targetId)) || null;
		},
		findChatByFriend: () => (fid) => {
			return chatLookup.get(resolveChatLookupKey('PRIVATE', fid)) || null;
		},
		findChatByGroup: () => (gid) => {
			return chatLookup.get(resolveChatLookupKey('GROUP', gid)) || null;
		},
		findMessage: (state) => (chat, msgInfo) => {
			if (!chat) {
				return null;
			}
			const runtimeIndex = resolveMessageRuntimeIndex(chat);
			if (runtimeIndex) {
				const indexedMessage = findMessageByRuntimeIndex(chat, msgInfo);
				if (indexedMessage) {
					return indexedMessage;
				}
				if (hasResolvableMessageLookupKey(chat, msgInfo)) {
					return null;
				}
			}
			if (chat.type == 'SYSTEM') {
				// 系统消息通过seqNo判断
				for (let idx in chat.messages) {
					if (msgInfo.seqNo && chat.messages[idx].seqNo == msgInfo.seqNo) {
						return chat.messages[idx];
					}
				}
			} else {
				// 通过id判断
				if (msgInfo.id) {
					for (let idx = chat.messages.length - 1; idx >= 0; idx--) {
						let m = chat.messages[idx];
						if (m.id && msgInfo.id == m.id) {
							return m;
						}
						// 如果id比要查询的消息小，说明没有这条消息
						if (m.id && m.id < msgInfo.id) {
							break;
						}
					}
				}
				// 正在发送中的临时消息可能没有id,只有tmpId
				if (msgInfo.selfSend && msgInfo.tmpId) {
					for (let idx = chat.messages.length - 1; idx >= 0; idx--) {
						let m = chat.messages[idx];
						if (!m.selfSend || !m.tmpId) {
							continue;
						}
						if (msgInfo.tmpId == m.tmpId) {
							return m;
						}
						// 如果id比要查询的消息小，说明没有这条消息
						if (m.tmpId && m.tmpId < msgInfo.tmpId) {
							break;
						}
					}
				}
			}
		},
			topSize: (state) => () => {
				void state.conversationStateVersion;
				let chats = state.findChats();
				return chats.filter((chat) => {
					const setting = state.conversationStates.get(resolveConversationKey(chat));
					return Boolean(setting && setting.isTop);
				}).length;
			}
	}
});
